import {
  alertAction,
  confirmAction,
  DCEvent,
  EventDispatcher,
  httpDelete,
  HttpEvent,
  httpGet,
  httpPost,
  httpPut,
  HttpResponse,
  loadUrl,
  navigate,
  showError,
  showGeneralError,
  showGeneralSuccess,
  showPaymentDialog,
  showSuccess,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import { RootStoreData } from "@/events/rootStoreData";
import {
  AddressForm,
  AddressType,
  addressUpdatedMessage,
  CompanyAddress,
  newAddressForm,
  toUpdateAddressRequest,
  UpdateAddressRequest,
} from "@/companyAddress/companyAddressDomain";
import { NewCompanyRejection } from "@/enterpriseAccounts/enterpriseAccountApiClient";
import {
  AltinnSystemUsersResponse,
  GetCompanySettingsResponse,
  ListAdminAccessResponse,
  OnboardingStatusResponse,
} from "@/types/Settings";
import { AppBarData } from "@/appBar/appBarDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { IconName } from "@/components-v2/iconMapping";
import { TMessage } from "@/i18n/tt";
import {
  adminPermissionsSettingsWithTab,
  adminSettings,
  adminUsersSettings,
  CompanySettingsRouteParams,
  customRoleCompanyUrl,
} from "@/utils/navigation";
import {
  Company,
  CompanyStatus,
  CompanyStatusDates,
  companyStatusLabels,
  EnterpriseAccountInfo,
  SuggestedCompany,
} from "@/types/CompanyInfo";
import {
  AccessToFeature,
  CustomRole,
  CustomRoleForUser,
  CustomRoleItem,
  CustomRoleLanguage,
  FeatureAccess,
  FeatureAccessValues,
  NewCustomRoleRequest,
  UpdateCustomRoleRequest,
} from "@/common/customRole";
import {
  BooleanFieldModel,
  createEmailField,
  documentLanguageFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
  YearFieldModel,
} from "@/common/commonForms";
import {
  AdminItem,
  Amount,
  CompanyItem,
  CompanyShareholderContact,
  DocumentLanguage,
  emailValidator,
  EnterpriseAccount,
  FairValue,
  Portfolio,
  ProductName,
  TaxAssessmentValue,
  UserItem,
} from "@/common/common";
import { getCountryName } from "@/utils/countries";
import { toLocalDateISOString } from "@/utils/Dates";
import { logDebug } from "@/utils/log";
import { compact, debounce, identity, isFunction, orderBy } from "lodash";
import { Language } from "@/i18n/languages";
import { defaultLanguageForOrgNo, isNorwegianOrgNo, isSwedishOrgNo } from "@/utils/orgNo";
import { TabButtonProps, TabProps } from "@/components-v2/Tabs";
import { TagProps } from "@/components-v2/Tag";
import { makeAutoObservable } from "mobx";
import { isNotBlank } from "@/utils/strings";
import { FeatureToggles } from "@/common/featureToggles";
import { FeatureName, Module, ModuleName } from "@/common/product";
import { PaidSubscription, SubscriptionStatusContext } from "@/common/subscription";
import { toggleElement } from "@/utils/arrays";
import { TMessageKey } from "@/i18n/types.gen";
import { newUuid } from "@/utils/uuid";
import { toQueryString } from "@/utils/queryParams";
import { formatUserName, User } from "@/types/UserInfo";
import { findInitialUrl } from "@/common/customRoleNavigation";
import { ShareType } from "@/common/share";
import {
  EnterpriseAccountsForUserResponse,
  ProductSubscriptionResponse,
  toTier,
} from "@/payment/Payment";
import {
  AuditorListItem,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  mapLocalesToDocumentLanguageWithDefault,
} from "@/dbot/common/commonDomain";
import {
  FileUploadConfig,
  isFileUploadValidationError,
  validateFiles,
} from "@/fileUpload/fileUploadDomain";
import { createFileTypeValidationErrorMessage } from "@/fileUpload/fileUploadErrorMessages";
import { InvitationType } from "@/types/Invitation";
import { PhoneInputModel } from "@/components-v2/PhoneInput";
import { formatOrgNum, personNameValidator } from "@/utils/other";
import { OnboardingStatus } from "@/onboardingstatus/OnboardingStatusDomain";
import {
  confirmDowngradeToOwnershipRegister,
  isDowngradeToOwnershipRegister,
  productNameLabel,
} from "@/enterpriseAccounts/enterpriseAccountsDomain";
import { PortfolioCompany } from "@/stores/PortfolioStore";
import { CompaniesRoleSelected } from "./usersComponents";
import { AutocompleteProps } from "@/components-v2/Autocomplete/Autocomplete";
import { NamedOrganisationNumber } from "@/types/Transactions";
import { RequestSystemUserResponse } from "@/api/AltinnApiService";
import { PortfolioCompaniesSelectProps } from "@/portfolio/companies/companiesDomain.ts";
import {
  DropdownMenuItemProps,
  DropdownMenuItems,
  DropdownMenuProps,
} from "@/components-v2/Dropdown.tsx";
import { roleTemplates } from "@/overview/companySettings/customRoleTemplates.ts";
import { TextWithTooltipProps } from "@/components-v2/TextWithTooltip.tsx";
import { RowAction } from "@/components-v2/DataTable";
import {
  CustomRoleDetailsModel,
  getRoleNameByLanguage,
} from "@/portfolio/contacts/customRoleDomain.ts";

export type CompanySettingsState = {
  isLoading?: boolean;
  canEdit?: boolean;
  isAdmin?: boolean;
  response?: GetCompanySettingsResponse;
  usersResponse?: ListAdminAccessResponse;
  altinnSystemUsers?: AltinnSystemUsersResponse;
  subscriptionResponse?: ProductSubscriptionResponse;
  enterpriseAccountsResponse?: EnterpriseAccountsForUserResponse;
  selectedMenuItem: CompanySettingsMenuItemType;
  editField?: CompanySettingsEditableField;
  usersTab?: UsersTab;
  selectedUserRoleFilter: UsersByRole;
  onboardStatusResult?: OnboardingStatusResponse;
  selectedCompaniesUsingRole: CompanyItem[];
  isLoadingAccountant?: boolean;
  isLoadingAuditor?: boolean;
  isInviteAccountantDialogOpen?: boolean;
  inviteAccountantEmail: StringFieldModel;
  isInviteAccountantDialogSubmitting?: boolean;
  showDeleteAccessDialogForUser?: User;
  isDeleteAccessDialogSubmitting?: boolean;
  validationError?: TMessage;
};

export type CompanyDetails = {
  companyName: string;
  brregCompanyName?: string;
  isNorwegianAS: boolean;
  organisationNumber: string;
  countryOfRegistration: string;
  entityType: string;
  subdivision: string;
  hasSubdivisions: boolean;
  dateOfIncorporation?: string;
  tinNumber: string;
  status: CompanyStatus;
  statusInfoText: TMessage;
};

export type AddressCardRow = { id: keyof CompanyAddress; label: TMessage; value: string };

export type AddressCard = {
  addressType: AddressType;
  rows: AddressCardRow[];
  effectiveFrom?: string;
};

export type RoleRow = { type: "admin" } | { type: "custom"; item: CustomRoleItem };

export type CompanySettingsViewProps = {
  appBar?: AppBarData | null;
  alertStore: AlertMessageStore;
  menuItems: CompanySettingsMenuItem[];
  selectedMenuItem: CompanySettingsMenuItemType;
  currency: string;
  isLoading?: boolean;
  canEdit: boolean;
  canInviteAdmin: boolean;
  orgNo: string;
  companyName: string;
  currentAccountant: NamedOrganisationNumber | undefined;
  currentAuditor: AuditorListItem | undefined;
  currentBusinessManager: NamedOrganisationNumber | undefined;
  editField?: CompanySettingsEditableField;
  onEditCompanyName: () => DCEvent[];
  onSaveCompanyName: () => DCEvent[];
  addressCards: AddressCard[];
  onEditAddress: (addressType: AddressType) => DCEvent[];
  onSaveAddress: () => DCEvent[];
  canEditCompanyStatus: boolean;
  openCompanyStatusDialog: () => DCEvent[];
  updateCompanyStatus: () => DCEvent[];
  onEditDateOfIncorporation: () => DCEvent[];
  onSaveDateOfIncorporation: () => DCEvent[];
  onEditTinNumber: () => DCEvent[];
  onSaveTinNumber: () => DCEvent[];
  canEditOrganisationNumber: boolean;
  onEditOrganisationNumber: () => DCEvent[];
  onSaveOrganisationNumber: () => DCEvent[];
  isCurrencyLocked: boolean;
  onEditCurrency: () => DCEvent[];
  onSaveCurrency: () => DCEvent[];
  showOnboardingStatus: boolean;
  confirmOnboardingCompanySettings: () => void;
  editAccountant: () => void;
  editAuditor: () => void;
  isLoadingAccountant?: boolean;
  onUpdateAccountant: () => void;
  isLoadingAuditor?: boolean;
  onUpdateAuditor: () => void;

  shareNominalValue?: number;
  cancelEdit: () => void;
  taxAssessmentValues: TaxAssessmentValue[];
  onEditTaxAssessment: (t: TaxAssessmentValue) => void;
  onAddNewTaxAssessment: DCEvent[];
  onDeleteTaxAssessmentValue: (taxAssessmentValueId: string) => DCEvent[];
  companyDetails: CompanyDetails;
  onSaveTaxAssessmentValue: () => DCEvent[];

  fairValues: FairValue[];
  findShareType: (sharetypeId: number) => ShareType;
  onEditFairValue: (f: FairValue) => DCEvent[];
  onAddNewFairValue: () => DCEvent[];
  saveFairValue: () => DCEvent[];
  onDeleteFairValue: (f: FairValue) => DCEvent[];

  documentLanguage: DocumentLanguage;
  onEditDocumentLanguage: () => DCEvent[];
  onSaveDocumentLanguage: () => DCEvent[];

  documentFont: DocumentFont;
  onEditDocumentFont: () => DCEvent[];
  onSaveDocumentFont: (font: DocumentFont) => DCEvent[];

  logoPlacement: LogoPlacementOption;
  onEditLogoPlacement: () => DCEvent[];
  onSaveLogoPlacement: (logoPlacement: LogoPlacementOption) => DCEvent[];

  fileUploadConfig: FileUploadConfig;
  logoBase64?: string;
  onUploadDocumentLogo: (files: File[]) => void;
  openRemoveDocumentLogoDialog: () => DCEvent[];
  onRemoveDocumentLogo: DCEvent[];

  whitelabelName?: string;
  onAddWhitelabel: () => DCEvent[];
  onEditWhitelabel: () => DCEvent[];
  onSaveWhitelabel: () => DCEvent[];
  onRemoveWhitelabel: () => DCEvent[];

  usersWithAdminAccess?: ListAdminAccessResponse;
  openSendInvitationDialog: (role?: CustomRole) => void;
  previewCustomRole: (role: CustomRole) => void;

  usersTabs: TabButtonProps[];
  usersTab?: UsersTab;
  openNewOrEditRoleDialog: (r?: CustomRoleItem) => void;
  deleteRole: (r: CustomRole) => DCEvent[];
  toggleRoleDescription: () => void;
  saveRole: DCEvent[];
  toggleExpandModule: (m: ModuleName) => void;
  toggleModule: (m: ModuleName, flag: boolean) => void;
  resetToAllAccess: (m: ModuleName) => DCEvent[];
  isAllFeaturesManageOrEdit: (m: ModuleName) => boolean;
  customRoles: CustomRoleItem[];
  usersByRoleModel: RadioFieldModel<UsersByRole>;
  users: UserListItem[];
  navigateToUsersWithFilter: (filter: UsersByRole) => DCEvent[];
  showAll: () => void;
  roleTabs: TabProps[];
  roleDetailsDialog: (customRoleItem: CustomRoleItem, locale: Language) => void;

  subscriptionContext: SubscriptionStatusContext;
  onUpgradeSubscription: () => DCEvent[];
  dangerZoneDelete: (type: DangerZoneDeleteType, title: TMessage, description: TMessage) => void;
  confirmDangerZoneDelete: () => void;
  openEnterpriseAccountDialog: (
    enterpriseAccountId?: string,
    productName?: ProductName,
  ) => DCEvent[];
  onSaveEnterpriseAccount: (request: UpdateEnterpriseAccountRequest) => DCEvent[];
  onRemoveFromAccount: () => DCEvent[];

  shareholderPortalEnabledLabel: TMessage;
  openToggleShareholderPortalDialog: () => DCEvent[];
  updateShareholderPortalEnabled: () => DCEvent[];

  shareholderContactFileUploadConfig: FileUploadConfig;
  shareholderContacts: CompanyShareholderContact[];
  openEditShareholderContactDialog: (shareholderContact?: CompanyShareholderContact) => DCEvent[];
  deleteShareholderContact: (id: string) => DCEvent[];
  editShareholderContact: () => void;
  companyShareholderContactImage: (id: string, ref: string) => string;
  emailNotificationProps?: EmailNotificationProps;
  selectedCompaniesUsingRole: CompanyItem[];

  hasIncompleteCostPriceHistory?: boolean;
  overrideCostPriceDisplay?: boolean;
  editOverrideCostPriceDisplay: () => void;
  updateOverrideCostPriceDisplay: () => void;

  altinnSystemUsers?: AltinnSystemUsersResponse;
  requestStandardSystemUser: () => void;
  requestClientSystemUser: () => void;
  accountantUsers?: User[];
  showInviteAccountantDialog: () => void;
  hideInviteAccountantDialog: () => void;
  isInviteAccountantDialogOpen?: boolean;
  inviteAccountantEmail: StringFieldModel;
  submitInviteAccountantDialog: () => void;
  isInviteAccountantDialogSubmitting?: boolean;
  openDeleteAccessDialog: (user: User) => void;
  showDeleteAccessDialogForUser?: User;
  hideDeleteAccessDialog: () => void;
  submitDeleteAccessDialog: () => void;
  isDeleteAccessDialogSubmitting?: boolean;
  validationError?: TMessage;
};

export type SigningReminderSettings = {
  reminderEnabled: boolean;
  firstReminderAfterDays: number;
  continueToSendReminderEveryDays: number;
  stopSendingReminderAfterDays: number;
  notifySigningNotCompletedEnabled: boolean;
  notifySigningNotCompletedAfterDays?: number;
  notifySigningNotCompletedRecipient?: ReminderRecipient;
};

export type EmailNotificationProps = {
  signingReminderSettings?: SigningReminderSettings;
  reminderEnabledModel: BooleanFieldModel;
  toggleAutomaticReminder: (props: EmailNotificationProps) => void;
  firstReminderAfterDaysModel: RadioFieldModel<number>;
  continueToSendReminderEveryDaysModel: RadioFieldModel<number>;
  stopSendingReminderAfterDaysModel: RadioFieldModel<number>;
  notifySigningNotCompletedEnabled: BooleanFieldModel;
  toggleNotifySigningNotCompletedEnabled: (props: EmailNotificationProps) => void;
  notifySigningNotCompletedModel: RadioFieldModel<ReminderRecipient>;
  notifySigningNotCompletedAfterDaysModel: RadioFieldModel<number>;
  onUpdateNumericField: (
    value: number,
    model: RadioFieldModel<number>,
    props: EmailNotificationProps,
  ) => void;
  onUpdateReminderRecipient: (
    value: ReminderRecipient,
    model: RadioFieldModel<ReminderRecipient>,
    props: EmailNotificationProps,
  ) => void;
};

type UserListItem = {
  key: string;
  name: string;
  email: string;
  tagLabel: TagProps;
  lastLogin?: string;
  revokeQuestion: TMessage;
  onRevoke?: () => void;
};

type UserByRoleType = "all_roles" | "administrators" | "custom_role";

export type UsersByRole = { type: UserByRoleType; label: TMessage; role?: CustomRole };

type UsersTab = "users" | "roles";

const usersTabLabels: { [key in UsersTab]: TMessage } = {
  roles: { key: "roles" },
  users: { key: "users" },
};

const usersTabTags: { [key in UsersTab]: TagProps | undefined } = {
  roles: { color: "info", label: { key: "new" } },
  users: undefined,
};

type DangerZoneDeleteType =
  | "deleteTransactions"
  | "deleteTransactionsAndShareholders"
  | "deregisterCompany"
  | "deleteAltinnSubmissions";

type CompanySettingsMenuItem = {
  title: TMessage;
  iconName: IconName;
  type: CompanySettingsMenuItemType;
  isSelected: boolean;
  badge?: TMessage;
};

export type CompanySettingsMenuItemType =
  | "company"
  | "shares"
  | "branding"
  | "permissions"
  | "users"
  | "shareholders"
  | "emailNotications"
  | "billing"
  | "altinn";

const companySettingsMenuItemLabels: { [key in CompanySettingsMenuItemType]: TMessage } = {
  company: { key: "company" },
  shares: { key: "dashboard.shares" },
  branding: { key: "settings.branding" },
  permissions: { key: "settings.permissions" },
  users: { key: "users" },
  shareholders: { key: "settings.shareholders" },
  emailNotications: { key: "settings.signing-reminder" },
  billing: { key: "settings.billing-and-account" },
  altinn: { key: "common.altinn" },
};

const companySettingsMenuItemIcons: { [key in CompanySettingsMenuItemType]: IconName } = {
  company: "buildings",
  shares: "article",
  branding: "paint-brush",
  permissions: "user-circle-gear",
  users: "users",
  shareholders: "users-three",
  emailNotications: "bell",
  billing: "credit-card",
  altinn: "plugs-connected",
};

export const companySettingsMenuItemTypes = (orgNo: string): CompanySettingsMenuItemType[] =>
  compact([
    "company",
    "shares",
    "branding",
    "permissions",
    "users",
    "shareholders",
    "emailNotications",
    isNorwegianOrgNo(orgNo) && "altinn",
    "billing",
  ]);

export const isoDatePrefix = (value?: string): string | undefined => {
  const candidate = value?.slice(0, 10) ?? "";
  return /^\d{4}-\d{2}-\d{2}$/.test(candidate) ? candidate : undefined;
};

export type CustomRoleTab = "allRoles" | "primaryCompany" | "otherCompany";

export type CompanySettingsEditableField =
  //company
  | {
      type: "companyName";
      currentValue: string;
      updateModel: StringFieldModel;
      effectiveFromModel: LocalDateFieldModel;
    }
  | {
      type: "address";
      form: AddressForm;
    }
  | {
      type: "companyStatus";
      model: RadioFieldModel<CompanyStatus>;
    }
  | {
      type: "dateOfIncorporation";
      updateModel: LocalDateFieldModel;
    }
  | {
      type: "tinNumber";
      updateModel: StringFieldModel;
      effectiveFromModel: LocalDateFieldModel;
    }
  | {
      type: "organisationNumber";
      updateModel: StringFieldModel;
      effectiveFromModel: LocalDateFieldModel;
    }
  | {
      type: "currency";
      updateModel: RadioFieldModel<string>;
    }
  | {
      type: "accountant";
      companySearchModel: StringFieldModel;
      updateModel: AutocompleteProps<SuggestedCompany | undefined>;
    }
  | {
      type: "auditor";
      updateModel: AutocompleteProps<SuggestedCompany | undefined>;
      companySearchModel: StringFieldModel;
    }

  //Branding
  | {
      type: "documentLanguage";
      updateModel: RadioFieldModel<DocumentLanguage>;
    }
  | {
      type: "documentFont";
      updateModel: RadioFieldModel<DocumentFont>;
    }
  | {
      type: "documentLogo";
      logoBase64?: string;
    }
  | {
      type: "removeDocumentLogoDialog";
    }
  | {
      type: "documentLogoPlacement";
      updateModel: RadioFieldModel<LogoPlacementOption>;
    }
  | {
      type: "whitelabel";
      whitelabelModel: StringFieldModel;
    }
  | {
      type: "taxAssessmentValue";
      data?: TaxAssessmentValue;
      yearModel?: YearFieldModel;
      currency?: RadioFieldModel<string>;
      assessmentValue?: NumberFieldModel;
      yearOptions: string[];
    }
  | {
      type: "fairValue";
      fairValueId: string;
      currency: string;
      fairValueDate: LocalDateFieldModel;
      shareType: RadioFieldModel<ShareType | null>;
      fairValue: NumberFieldModel;
    }

  //users
  | RoleDialogModel
  | {
      type: "dangerZoneDelete";
      dangerZone: DangerZoneDeleteType;
      title: TMessage;
      description: TMessage;
      confirmationInput: StringFieldModel;
    }
  | { type: "toggleShareholderPortal"; model: RadioFieldModel<boolean> }
  | {
      type: "editShareholderContact";
      id?: string;
      name: StringFieldModel;
      title: StringFieldModel;
      email: StringFieldModel;
      phoneNumber: PhoneInputModel;
      linkedin: StringFieldModel;
      imageReference?: string;
      onUploadShareholderContactImage: (files: File[]) => void;
      onDeleteShareholderContactImage: DCEvent[];
      uploadingImage?: File;
    }
  | { type: "overrideCostPriceDisplay"; model: RadioFieldModel<boolean> }
  | { type: "editEnterpriseAccount"; model: EditEnterpriseAccountModel };

export type EditEnterpriseAccountModel = {
  enterpriseAccount: RadioFieldModel<EnterpriseAccount>;
  productName: RadioFieldModel<ProductName>;
};

type RoleDialogModel = {
  type: "roleDialog";
  companyName: string;
  roleLanguageModels: CustomRoleLanguageModel[];
  showDescription: boolean;
  filteredModulesBySubscription: Module[];
  availableFeaturesBasedOnCountry: { [key in FeatureName]: boolean };
  enabledModules: ModuleName[];
  expandedModules: ModuleName[];
  modules: Module[];
  featureAccesses: { [key in FeatureName]: RadioFieldModel<FeatureAccess> };
  featureUnavailableInfoLabel: TMessageKey;
  data?: CustomRole;
  companiesUsingRole: CompanyItem[];
  usersUsingRole: UserItem[];
  roleSharing: RadioFieldModel<boolean>;
  toggleViewEditRoleSelection: DCEvent[];
  updateSelectedCompaniesUsingRole: (selectedCompanies: string[]) => void;
  showViewEditRoleSelection: boolean;
  portfolioCompaniesSelectItem: PortfolioCompaniesSelectProps;
  companiesRoleSelected: OptionFieldModel<PortfolioCompany>;
};

export const prepCompanySettingsProps = ({
  appBar,
  alertStore,
  state = defaultCompanySettingsState(),
  company,
  subscription,
  featureToggles,
  dispatch,
  locale,
  user,
  enterpriseAccountInfo,
  portfolioCompanies,
  portfolios,
  roleTab,
}: {
  appBar: AppBarData | null;
  alertStore: AlertMessageStore;
  state?: CompanySettingsState;
  company: Company;
  locale: Language;
  subscription?: PaidSubscription;
  featureToggles: FeatureToggles;
  dispatch: (events: DCEvent[]) => void;
  user: User;
  enterpriseAccountInfo?: EnterpriseAccountInfo;
  portfolioCompanies: PortfolioCompany[];
  portfolios: Portfolio[];
  roleTab: CustomRoleTab;
}): CompanySettingsViewProps => {
  const orgNo = company.organizationnumber;
  const menuItems = prepMenuItems(state, orgNo);
  const currency = state.response?.company.currency ?? company.currency;
  const companyName = company.name;
  const customRoles: CustomRoleItem[] = state.response?.customRoles ?? [];
  const isNorwegianAS = state.response?.isNorwegianAS ?? false;
  const usersByRoleModel = createUsersByRoleModel(
    dispatch,
    customRoles,
    state.selectedUserRoleFilter,
  );
  const fileUploadConfig: FileUploadConfig = {
    supportedFileTypes: [{ extensions: ["jpg", "jpeg", "gif", "png"], magicBytes: [] }],
    maxFileSize: 200 * 1024, // 100 KB
    maxDimensions: { width: 1600, height: 1600 },
  };
  const shareholderContactFileUploadConfig: FileUploadConfig = {
    supportedFileTypes: [{ extensions: ["jpg", "jpeg", "gif", "png"], magicBytes: [] }],
    maxFileSize: 1024 * 1024,
    maxDimensions: { width: 1600, height: 1600 },
  };
  const logoPlacement = state.response?.documentSettings?.logoPlacement ?? "center";
  const companyStatus = state.response?.company.status ?? CompanyStatus.ACTIVE;
  const dateOfIncorporation = isoDatePrefix(state.response?.company.created);
  const addresses = companyAddresses(state.response);
  const selectedMenuItem = menuItems.find((m) => m.isSelected)!.type;
  return {
    appBar,
    alertStore,
    menuItems,
    selectedMenuItem,
    currency,
    isLoading: state.isLoading,
    canEdit: state.canEdit ?? true,
    canInviteAdmin: state.isAdmin ?? true,
    editField: state.editField,
    shareNominalValue: state.response?.shareNominalValue,
    //Company info stuff
    onEditCompanyName: () => editCompanyName(state.response?.company.name ?? "", isNorwegianAS),
    companyDetails: {
      companyName: state.response?.company.name ?? "",
      brregCompanyName: state.response?.brregCompanyName,
      isNorwegianAS,
      organisationNumber: formatOrgNum(orgNo),
      countryOfRegistration: getCountryName(company.country, locale) ?? "",
      entityType:
        state.response?.organisationTypeLabel?.[locale] ??
        state.response?.company.organisationType ??
        "",
      subdivision:
        state.response?.registrationSubdivisionLabel?.[locale] ??
        state.response?.company.registrationSubdivision ??
        "",
      hasSubdivisions: state.response?.hasRegistrationSubdivisions ?? false,
      dateOfIncorporation,
      tinNumber: state.response?.company.tinNumber ?? "",
      status: companyStatus,
      statusInfoText: companyStatusInfoText(
        companyStatus,
        dateOfIncorporation,
        state.response?.companyStatusDates,
        appBar?.liquidationInfoText,
      ),
    },
    onSaveCompanyName: () =>
      onSaveCompanyField({
        orgNo,
        data: toUpdateCompanyInfoRequest(state),
      }),
    addressCards: addressCards(state.response, locale),
    onEditAddress: (addressType) => editAddress(addressType, addresses[addressType], locale),
    onSaveAddress: () => saveAddress(orgNo, state),
    canEditCompanyStatus: state.response?.canEditCompanyStatus ?? false,
    openCompanyStatusDialog: () => openCompanyStatusDialog(state),
    updateCompanyStatus: () => updateCompanyStatus(orgNo, state),
    onEditDateOfIncorporation: () => editDateOfIncorporation(dateOfIncorporation),
    onSaveDateOfIncorporation: () => updateDateOfIncorporation(orgNo, state),
    onEditTinNumber: () => editTinNumber(state.response?.company.tinNumber),
    onSaveTinNumber: () => updateTinNumber(orgNo, state),
    canEditOrganisationNumber: state.response?.canEditOrganisationNumber ?? false,
    onEditOrganisationNumber: () =>
      editOrganisationNumber(organisationNumberIdentifier(orgNo, company.country)),
    onSaveOrganisationNumber: () => updateOrganisationNumber(company, state),
    isCurrencyLocked: state.response?.isCurrencyLocked ?? true,
    onEditCurrency: () => editCurrency(currency),
    onSaveCurrency: () => updateCurrency(orgNo, state),

    orgNo,
    companyName,
    currentAccountant: state.response?.currentAccountant,
    currentAuditor: state.response?.currentAuditor,
    currentBusinessManager: state.response?.currentBusinessManager,
    showOnboardingStatus: state.onboardStatusResult?.settings === OnboardingStatus.incomplete,
    confirmOnboardingCompanySettings: () => dispatch(confirmOnboardingCompanySettings(orgNo)),
    editAccountant: () =>
      dispatch(
        onEditAccountant({
          dispatch,
          orgNo,
          currentAccountant: state.response?.currentAccountant,
        }),
      ),
    editAuditor: () =>
      dispatch(
        onEditAuditor({
          dispatch,
          orgNo,
          currentAuditor: state.response?.currentAuditor,
        }),
      ),
    isLoadingAccountant: state.isLoadingAccountant,
    isLoadingAuditor: state.isLoadingAuditor,
    onUpdateAccountant: () => dispatch(updateAccountant(orgNo, state.editField)),
    onUpdateAuditor: () => dispatch(updateAuditor(orgNo, state.editField)),

    cancelEdit: () => dispatch([setInCompanySettingsState("editField", undefined)]),
    //tax assessment stuff
    taxAssessmentValues: state.response?.taxAssessmentValues ?? [],
    onAddNewTaxAssessment: onAddNewTaxAssessment(currency),
    onEditTaxAssessment: (t: TaxAssessmentValue) => dispatch(onEditTaxAssessment(t)),
    onDeleteTaxAssessmentValue: (id) => deleteTaxAssessmentValue(orgNo, id),
    onSaveTaxAssessmentValue: () => saveTaxAssessmentValue(orgNo, currency, state),

    //fairValue stuff
    fairValues: state.response?.fairValues ?? [],
    findShareType: (sharetypeId: number) =>
      findShareType(state.response?.shareTypes ?? [], sharetypeId),
    onEditFairValue: (value: FairValue) =>
      onEditFairValue(currency, state.response?.shareTypes ?? [], value),
    onAddNewFairValue: () => onEditFairValue(currency, state.response?.shareTypes ?? []),
    saveFairValue: () => saveFairValue(orgNo, currency, state),
    onDeleteFairValue: (f: FairValue) => onDeleteFairValue(orgNo, f.fairValueId),

    //Branding stuff
    documentLanguage: mapLocalesToDocumentLanguageWithDefault(
      orgNo,
      state.editField?.type === "documentLanguage"
        ? mapDocumentLanguageToLocales(state.editField.updateModel.value)
        : state.response?.documentSettings?.defaultDocumentLanguages,
    ),
    onEditDocumentLanguage: () =>
      onEditDocumentLanguage(orgNo, state.response?.documentSettings?.defaultDocumentLanguages),
    onSaveDocumentLanguage: () =>
      state.editField?.type === "documentLanguage"
        ? onSaveDocumentLanguage(orgNo, state.editField.updateModel.value)
        : [],

    documentFont: state.response?.documentSettings?.font as DocumentFont,
    onEditDocumentFont: () =>
      onEditDocumentFont(
        state.response?.supportedFonts ?? [],
        state.response?.documentSettings?.font,
      ),
    onSaveDocumentFont: (documentFont) => onSaveDocumentFont(orgNo, documentFont),

    logoPlacement,
    onEditLogoPlacement: () => onEditLogoPlacement(logoPlacement),
    onSaveLogoPlacement: (logoPlacement) => onSaveDocumentPlacement(orgNo, logoPlacement),

    fileUploadConfig,
    logoBase64: state.response?.documentSettings?.logoBase64,
    onUploadDocumentLogo: (files) => {
      onUploadDocumentLogo(dispatch, orgNo, files, fileUploadConfig);
    },
    openRemoveDocumentLogoDialog: () => onOpenRemoveDocumentLogoDialog(),
    onRemoveDocumentLogo: onRemoveDocumentLogo(orgNo),

    whitelabelName: state.response?.company.whitelabel?.name,
    onAddWhitelabel: () => addWhitelabel(),
    onEditWhitelabel: () => onEditWhitelabel(state.response?.company.whitelabel?.name),
    onSaveWhitelabel: () =>
      onSaveWhitelabel(
        orgNo,
        state.editField?.type === "whitelabel"
          ? state.editField.whitelabelModel.validatedValue
          : "",
      ),
    onRemoveWhitelabel: () => onRemoveWhitelabel(orgNo),

    //administrator stuff
    usersWithAdminAccess: state.usersResponse,
    usersTabs: ["users", "roles"].map<TabButtonProps>((tab) => ({
      isActive: state.usersTab === undefined ? tab === "users" : state.usersTab === tab,
      onSelect: () =>
        dispatch([
          setInCompanySettingsState("usersTab", tab as UsersTab),
          setInCompanySettingsState("isLoading", true),
          loadUsersWithAdminAccess(orgNo),
        ]),
      id: tab,
      label: usersTabLabels[tab as UsersTab],
      tag: usersTabTags[tab as UsersTab],
    })),
    openSendInvitationDialog: (role?: CustomRole) =>
      dispatch(
        openSendInvitationDialog({
          organisationNumber: company.organizationnumber,
          companyName: company.name,
          role,
          customRoles,
        }),
      ),
    previewCustomRole: (role: CustomRole) => dispatch(previewCustomRole(role, orgNo)),

    usersTab: state.usersTab ?? "users",
    openNewOrEditRoleDialog: (r?: CustomRoleItem) =>
      dispatch(
        openNewOrEditRoleDialog(
          portfolios,
          portfolioCompanies,
          company,
          state,
          dispatch,
          featureToggles,
          subscription,
          r,
        ),
      ),
    deleteRole: (r: CustomRole) => deleteCustomRole(orgNo, r),
    toggleRoleDescription: () => dispatch(toggleRoleDescription(state)),
    saveRole: saveRole(state, orgNo),
    toggleExpandModule: (m: ModuleName) => dispatch(toggleExpandModule(m, state)),
    toggleModule: (m: ModuleName, flag: boolean) => dispatch(toggleModule(m, flag, state)),
    resetToAllAccess: (m: ModuleName) => resetToAllAccess(m, state),
    isAllFeaturesManageOrEdit: (m: ModuleName): boolean => isAllFeaturesManageOrEdit(m, state),
    customRoles,
    usersByRoleModel,
    users: prepUsers(
      dispatch,
      orgNo,
      customRoles,
      state.selectedUserRoleFilter,
      state.usersResponse?.admins,
    ),
    navigateToUsersWithFilter: (filter: UsersByRole) => [
      navigate(adminUsersSettings(orgNo)),
      setInCompanySettingsState("selectedMenuItem", "users"),
      setInCompanySettingsState("selectedUserRoleFilter", filter),
    ],
    showAll: () =>
      dispatch([
        setInCompanySettingsState("selectedUserRoleFilter", {
          type: "all_roles",
          label: { key: "all-roles" },
        }),
      ]),
    roleTabs: prepRoleTabs({
      tab: roleTab,
      dispatch,
      primaryCompanyName: companyName,
      orgNo,
      customRoles: customRoles,
    }),
    roleDetailsDialog: (customRoleItem, language) =>
      roleDetailsDialog({
        customRoleItem,
        locale: language,
        dispatch,
        orgNo,
        portfolioCompanies,
      }),

    // billing and account
    subscriptionContext: prepSubscriptionContext(company, user, state, enterpriseAccountInfo),
    onUpgradeSubscription: () => [showPaymentDialog],
    dangerZoneDelete: (type: DangerZoneDeleteType, title: TMessage, description: TMessage) => [
      setInCompanySettingsState("editField", {
        type: "dangerZoneDelete",
        dangerZone: type,
        title,
        description,
        confirmationInput: new StringFieldModel(),
      }),
    ],
    openEnterpriseAccountDialog: (enterpriseAccountId?: string, productName?: ProductName) =>
      compact([
        state.enterpriseAccountsResponse
          ? setInCompanySettingsState("editField", {
              type: "editEnterpriseAccount",
              model: {
                enterpriseAccount: new RadioFieldModel<EnterpriseAccount>({
                  value:
                    state.enterpriseAccountsResponse.enterpriseAccounts.find(
                      (a) => a.id === enterpriseAccountId,
                    ) ?? state.enterpriseAccountsResponse.enterpriseAccounts[0],
                  options: [...(state.enterpriseAccountsResponse?.enterpriseAccounts ?? [])],
                  idFn: (value) => value?.id ?? "",
                  labelFn: (v) => (v ? { translated: v.name } : { translated: "" }),
                }),
                productName: new RadioFieldModel<ProductName>({
                  value: productName ?? "digital_company",
                  options: compact([
                    isNorwegianOrgNo(orgNo) ? "digital_company" : undefined,
                    "shareholder_register",
                    "ownership_register",
                  ]),
                  idFn: (value) => value ?? "",
                  labelFn: (value) => productNameLabel(value),
                }),
              },
            })
          : undefined,
      ]),
    onSaveEnterpriseAccount: (request: UpdateEnterpriseAccountRequest) =>
      onSaveEnterpriseAccount(orgNo, state.subscriptionResponse?.productName, request),
    onRemoveFromAccount: () => onRemoveFromAccount(orgNo),
    confirmDangerZoneDelete: () => dispatch(confirmDangerZoneDelete(orgNo, state)),
    shareholderPortalEnabledLabel: {
      key:
        state.response?.isShareholderPortalEnabled === false
          ? "settings.shareholder-portal.disabled"
          : "settings.shareholder-portal.enabled",
    },
    openToggleShareholderPortalDialog: () => [
      setInCompanySettingsState("editField", {
        type: "toggleShareholderPortal",
        model: new RadioFieldModel<boolean>({
          value: state.response?.isShareholderPortalEnabled !== false,
          options: [true, false],
          idFn: String,
          labelFn: (v) => ({
            key: v ? "settings.shareholder-portal.enabled" : "settings.shareholder-portal.disabled",
          }),
        }),
      }),
    ],
    updateShareholderPortalEnabled: () => updateShareholderPortalEnabled(orgNo, state),

    //shareholderContact stuff
    shareholderContactFileUploadConfig,
    shareholderContacts: state.response?.shareholderContacts ?? [],
    openEditShareholderContactDialog: (contact) => [
      setInCompanySettingsState("editField", {
        type: "editShareholderContact",
        id: contact?.id,
        name: new StringFieldModel({
          value: contact?.name,
          required: true,
          validator: personNameValidator,
        }),
        title: new StringFieldModel({ value: contact?.title }),
        email: createEmailField({ value: contact?.email, required: true }),
        phoneNumber: new PhoneInputModel({ value: contact?.phoneNumber }),
        linkedin: new StringFieldModel({ value: contact?.linkedin?.replace("https://", "") }),
        imageReference: contact?.imageReference,
        onUploadShareholderContactImage: (files: File[]) => {
          validateFiles(files, shareholderContactFileUploadConfig)
            .then((files) => {
              dispatch([
                setInCompanySettingsState("editField", (companySettingsState) =>
                  companySettingsState.editField?.type === "editShareholderContact"
                    ? {
                        ...companySettingsState.editField!,
                        uploadingImage: files[0],
                      }
                    : undefined,
                ),
              ]);
            })
            .catch((err) => {
              if (isFileUploadValidationError(err)) {
                dispatch([showError(createFileTypeValidationErrorMessage(err))]);
              }
            });
        },
        onDeleteShareholderContactImage: [
          setInCompanySettingsState("editField", (companySettingsState) =>
            companySettingsState.editField?.type === "editShareholderContact"
              ? {
                  ...companySettingsState.editField!,
                  imageReference: undefined,
                  uploadingImage: undefined,
                }
              : undefined,
          ),
        ],
      }),
    ],
    editShareholderContact: () => dispatch(editShareholderContact(orgNo, state)),
    deleteShareholderContact: (id) => deleteShareholderContact(orgNo, id),
    companyShareholderContactImage: (id, ref) => companyShareholderContactImageUrl(orgNo, id, ref),
    emailNotificationProps: prepSigningReminderSettingsProps(orgNo, state, dispatch),
    selectedCompaniesUsingRole: state.selectedCompaniesUsingRole,
    hasIncompleteCostPriceHistory: state.response?.hasIncompleteCostPriceHistory,
    overrideCostPriceDisplay: state.response?.overrideCostPriceDisplay,
    editOverrideCostPriceDisplay: () => dispatch(editOverrideCostPriceDisplay(state)),
    updateOverrideCostPriceDisplay: () => dispatch(updateOverrideCostPriceDisplay(orgNo, state)),
    altinnSystemUsers: state.altinnSystemUsers,
    requestStandardSystemUser: () => dispatch(requestStandardSystemAccess(orgNo)),
    requestClientSystemUser: () => dispatch(requestClientSystemAccess(orgNo)),
    accountantUsers: state.response?.accountantUsers,
    showInviteAccountantDialog: () => dispatch(showInviteAccountantDialog()),
    hideInviteAccountantDialog: () =>
      dispatch(hideInviteAccountantDialog(state.inviteAccountantEmail)),
    isInviteAccountantDialogOpen: state.isInviteAccountantDialogOpen,
    inviteAccountantEmail: state.inviteAccountantEmail,
    submitInviteAccountantDialog: () =>
      dispatch(submitInviteAccountantDialog(orgNo, state.inviteAccountantEmail, locale)),
    isInviteAccountantDialogSubmitting: state.isInviteAccountantDialogSubmitting,
    openDeleteAccessDialog: (user: User) => dispatch(openDeleteAccessDialog(user)),
    showDeleteAccessDialogForUser: state.showDeleteAccessDialogForUser,
    hideDeleteAccessDialog: () => dispatch(hideDeleteAccessDialog()),
    submitDeleteAccessDialog: () =>
      dispatch(submitDeleteAccessDialog(orgNo, state.showDeleteAccessDialogForUser?.userId)),
    isDeleteAccessDialogSubmitting: state.isDeleteAccessDialogSubmitting,
    validationError: state.validationError,
  };
};

const roleDetailsDialog = ({
  customRoleItem,
  locale,
  dispatch,
  orgNo,
  portfolioCompanies,
}: {
  customRoleItem: CustomRoleItem;
  locale: Language;
  dispatch: EventDispatcher;
  orgNo: string;
  portfolioCompanies?: PortfolioCompany[];
}) => {
  const onClose = () => dispatch([{ type: "closeDialog" }]);
  return dispatch([
    {
      type: "show-dialog",
      dialog: {
        type: "open-role-details",
        model: new CustomRoleDetailsModel({
          hasAdminInCreatorCompany:
            customRoleItem.role.orgNo === orgNo ||
            (portfolioCompanies?.some((c) => c.organisationNumber === customRoleItem.role.orgNo) ??
              false),
          customRoleItem,
          onClose,
          locale,
        }),
      },
    },
  ]);
};

function createSharingModel(roleItem: CustomRoleItem | undefined): RadioFieldModel<boolean> {
  const roleIsInUseByOtherOrgNos =
    (roleItem?.users?.map((u) => u.orgNo)?.filter((o) => o !== roleItem?.role?.orgNo).length ?? 0) >
    0;
  logDebug("roleItem", roleItem);
  logDebug("roleIsInUseByOtherOrgNos", roleIsInUseByOtherOrgNos);
  return new RadioFieldModel({
    value: (roleItem?.companies?.length ?? 1) > 1,
    labelFn: identity, // overridden via MRadioButtonModel in the components
    options: [false, true],
    idFn: identity,
    disabledFn: (shared) => {
      logDebug("shared", shared);
      return !shared && roleIsInUseByOtherOrgNos;
    },
  });
}

const prepSigningReminderSettingsProps = (
  orgNo: string,
  state: CompanySettingsState,
  dispatch: (events: DCEvent[]) => void,
): EmailNotificationProps => {
  const firstReminderAfterDays =
    state.response?.signingReminderSettings?.firstReminderAfterDays ?? 3;
  const continueToSendReminderEveryDays =
    state.response?.signingReminderSettings?.continueToSendReminderEveryDays ?? 10;
  const stopSendingReminderAfterDays =
    state.response?.signingReminderSettings?.stopSendingReminderAfterDays ?? 30;
  const notifySigningNotCompleted =
    state.response?.signingReminderSettings?.notifySigningNotCompletedRecipient ??
    ReminderRecipient.DOCUMENT_SENDER;
  const notifySigningNotCompletedAfterDays =
    state.response?.signingReminderSettings?.notifySigningNotCompletedAfterDays ?? 10;

  return {
    signingReminderSettings: state.response?.signingReminderSettings,
    reminderEnabledModel: new BooleanFieldModel({
      value: state.response?.signingReminderSettings?.reminderEnabled,
    }),
    toggleAutomaticReminder: (props) => toggleReminderEnabled(orgNo, props, dispatch),
    firstReminderAfterDaysModel: numericModel(firstReminderAfterDays),
    continueToSendReminderEveryDaysModel: numericModel(continueToSendReminderEveryDays, 1, 20),
    stopSendingReminderAfterDaysModel: numericModel(stopSendingReminderAfterDays, 2, 40),
    notifySigningNotCompletedEnabled: new BooleanFieldModel({
      value: state.response?.signingReminderSettings?.notifySigningNotCompletedEnabled,
    }),
    toggleNotifySigningNotCompletedEnabled: (props) =>
      toggleNotifySigningNotCompletedEnabled(orgNo, props, dispatch),
    notifySigningNotCompletedModel: reminderRecipientModel(notifySigningNotCompleted),
    notifySigningNotCompletedAfterDaysModel: numericModel(notifySigningNotCompletedAfterDays),
    onUpdateNumericField: (value, model, props) =>
      onUpdateNumericField(value, model, orgNo, props, dispatch),
    onUpdateReminderRecipient: (value, model, props) =>
      onUpdateReminderRecipient(value, model, orgNo, props, dispatch),
  };
};

const onUpdateNumericField = (
  value: number,
  model: RadioFieldModel<number>,
  orgNo: string,
  props: EmailNotificationProps,
  dispatch: (events: DCEvent[]) => void,
) => {
  model.setValue(value);
  postReminderSettingsUpdate(orgNo, props, dispatch);
};

const onUpdateReminderRecipient = (
  value: ReminderRecipient,
  model: RadioFieldModel<ReminderRecipient>,
  orgNo: string,
  props: EmailNotificationProps,
  dispatch: (events: DCEvent[]) => void,
) => {
  model.setValue(value);
  postReminderSettingsUpdate(orgNo, props, dispatch);
};

const toggleReminderEnabled = (
  orgNo: string,
  props: EmailNotificationProps,
  dispatch: EventDispatcher,
) => {
  props.reminderEnabledModel.setValue(!props.reminderEnabledModel.value);
  postReminderSettingsUpdate(orgNo, props, dispatch);
};

const toggleNotifySigningNotCompletedEnabled = (
  orgNo: string,
  props: EmailNotificationProps,
  dispatch: EventDispatcher,
) => {
  props.notifySigningNotCompletedEnabled.setValue(!props.notifySigningNotCompletedEnabled.value);
  postReminderSettingsUpdate(orgNo, props, dispatch);
};

const postReminderSettingsUpdate = (
  orgNo: string,
  props: EmailNotificationProps,
  dispatch: EventDispatcher,
) => {
  const signingReminderSettings: SigningReminderSettings = {
    reminderEnabled: props.reminderEnabledModel.value ?? false,
    notifySigningNotCompletedEnabled: props.notifySigningNotCompletedEnabled.value ?? false,
    firstReminderAfterDays: props.firstReminderAfterDaysModel.value,
    continueToSendReminderEveryDays: props.continueToSendReminderEveryDaysModel.value,
    stopSendingReminderAfterDays: props.stopSendingReminderAfterDaysModel.value,
    notifySigningNotCompletedRecipient: props.notifySigningNotCompletedModel.value,
    notifySigningNotCompletedAfterDays: props.notifySigningNotCompletedAfterDaysModel.value,
  };
  dispatch([
    settingsApi.updateSigningReminderSettings({
      orgNo,
      signingReminderSettings,
      onSuccess: () => [showGeneralSuccess()],
      onFailure: () => [showGeneralError()],
    }),
  ]);
};

const updateShareholderPortalEnabled = (orgNo: string, state: CompanySettingsState): DCEvent[] => {
  if (state.editField?.type === "toggleShareholderPortal") {
    const isShareholderPortalEnabled = state.editField.model.value;
    return [
      settingsApi.updateCompanySettings({
        orgNo,
        data: { isShareholderPortalEnabled },
        onSuccess: updateSettingsSuccess(orgNo),
        onFailure: () => [showGeneralError()],
      }),
    ];
  }
  return [];
};

export const companyStatusInfoText = (
  status: CompanyStatus,
  dateOfIncorporation: string | undefined,
  dates: CompanyStatusDates | undefined,
  liquidationInfoText: TMessage | undefined,
): TMessage => {
  switch (status) {
    case CompanyStatus.ACTIVE:
      return { key: "settings.company-status.info.ACTIVE" };
    case CompanyStatus.UNDER_INCORPORATION:
      return dateOfIncorporation
        ? {
            key: "settings.company-status.info.UNDER_INCORPORATION",
            params: { signedDate: { type: "date", value: dateOfIncorporation } },
          }
        : { key: "settings.company-status.info.UNDER_INCORPORATION.no-dates" };
    case CompanyStatus.IN_LIQUIDATION:
      return dates?.dissolutionRegisteredDate && dates.creditorNoticeExpiryDate
        ? {
            key: "settings.company-status.info.IN_LIQUIDATION",
            params: {
              registeredDate: { type: "date", value: dates.dissolutionRegisteredDate },
              expiryDate: { type: "date", value: dates.creditorNoticeExpiryDate },
            },
          }
        : { key: "settings.company-status.info.IN_LIQUIDATION.no-dates" };
    case CompanyStatus.LIQUIDATED:
      return liquidationInfoText ?? { key: "settings.company-status.info.LIQUIDATED" };
    case CompanyStatus.SHELVED:
      return { key: "settings.company-status.info.SHELVED" };
    case CompanyStatus.DIVESTED:
      return { key: "settings.company-status.info.DIVESTED" };
    case CompanyStatus.MERGED:
      return { key: "settings.company-status.info.MERGED" };
  }
};

export const openCompanyStatusDialog = (state: CompanySettingsState): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "companyStatus",
    model: new RadioFieldModel<CompanyStatus>({
      value: state.response?.company.status ?? CompanyStatus.ACTIVE,
      options: Object.values(CompanyStatus),
      idFn: String,
      labelFn: (v) => ({ key: companyStatusLabels[v ?? CompanyStatus.ACTIVE] }),
    }),
  }),
];

export const updateCompanyStatus = (orgNo: string, state: CompanySettingsState): DCEvent[] => {
  if (state.editField?.type === "companyStatus") {
    return [
      settingsApi.updateCompanyStatus({
        orgNo,
        data: { status: state.editField.model.value ?? CompanyStatus.ACTIVE },
        onSuccess: updateSettingsSuccess(orgNo),
        onFailure: () => [showGeneralError()],
      }),
    ];
  }
  return [];
};

export const editDateOfIncorporation = (dateOfIncorporation?: string): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "dateOfIncorporation",
    updateModel: new LocalDateFieldModel({
      value: dateOfIncorporation,
      required: true,
      maxDate: toLocalDateISOString(Date.now()),
    }),
  }),
];

export const updateDateOfIncorporation = (
  orgNo: string,
  state: CompanySettingsState,
): DCEvent[] => {
  const dateOfIncorporation =
    state.editField?.type === "dateOfIncorporation"
      ? state.editField.updateModel.validatedValue
      : undefined;
  if (!dateOfIncorporation) {
    return [];
  }
  return [
    settingsApi.updateDateOfIncorporation({
      orgNo,
      data: { dateOfIncorporation },
      onSuccess: updateSettingsSuccess(orgNo),
      onFailure: () => [showGeneralError()],
    }),
  ];
};

export const editTinNumber = (tinNumber?: string): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "tinNumber",
    updateModel: new StringFieldModel({ value: tinNumber ?? "" }),
    effectiveFromModel: new LocalDateFieldModel({ maxDate: toLocalDateISOString(Date.now()) }),
  }),
];

export const updateTinNumber = (orgNo: string, state: CompanySettingsState): DCEvent[] => {
  if (state.editField?.type !== "tinNumber") {
    return [];
  }
  return [
    settingsApi.updateTinNumber({
      orgNo,
      data: {
        tinNumber: state.editField.updateModel.validatedValue.trim() || null,
        effectiveFrom: state.editField.effectiveFromModel.validatedValue,
      },
      onSuccess: updateSettingsSuccess(orgNo),
      onFailure: () => [showGeneralError()],
    }),
  ];
};

export const organisationNumberIdentifier = (orgNo: string, country: string): string =>
  country && orgNo.toUpperCase().startsWith(country.toUpperCase())
    ? orgNo.slice(country.length)
    : orgNo;

export const editOrganisationNumber = (identifier: string): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "organisationNumber",
    updateModel: new StringFieldModel({ required: true, value: identifier }),
    effectiveFromModel: new LocalDateFieldModel({ maxDate: toLocalDateISOString(Date.now()) }),
  }),
];

export const updateOrganisationNumber = (
  { organizationnumber: orgNo, companyId }: Company,
  state: CompanySettingsState,
): DCEvent[] => {
  if (state.editField?.type !== "organisationNumber") {
    return [];
  }
  const { updateModel, effectiveFromModel } = state.editField;
  return [
    settingsApi.updateOrganisationNumber({
      companyId,
      data: {
        oldOrganisationNumber: orgNo,
        newOrganisationNumber: updateModel.validatedValue,
        effectiveFrom: effectiveFromModel.validatedValue,
      },
      onSuccess: (res, rootState) =>
        res.data.organisationNumber === orgNo
          ? updateSettingsSuccess(orgNo)(res, rootState)
          : [loadUrl(adminSettings(res.data.organisationNumber), false)],
      onFailure: (err) => {
        const error = organisationNumberRejection(err);
        if (!error) {
          return [showGeneralError()];
        }
        updateModel.setError(error);
        return [];
      },
    }),
  ];
};

const organisationNumberRejection = (err: unknown): TMessage | undefined => {
  const rejection = (err as { response?: { data?: NewCompanyRejection } })?.response?.data;
  switch (rejection?.code) {
    case "INVALID_IDENTIFIER":
      return {
        key: "add-company.organisation-number-format",
        params: { example: rejection.expectedExample ?? "" },
      };
    case "ALREADY_EXISTS":
      return { key: "settings.updateOrganisationNumber.taken" };
    default:
      return undefined;
  }
};

export const editCurrency = (currency: string): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "currency",
    updateModel: currencyModel(currency),
  }),
];

export const updateCurrency = (orgNo: string, state: CompanySettingsState): DCEvent[] => {
  const currency =
    state.editField?.type === "currency" ? state.editField.updateModel.value : undefined;
  if (!currency) {
    return [];
  }
  return [
    settingsApi.updateCurrency({
      orgNo,
      data: { currency },
      onSuccess: (res, rootState) => [
        ...updateSettingsSuccess(orgNo)(res, rootState),
        { type: "update-company-info" },
      ],
      onFailure: () => [showGeneralError()],
    }),
  ];
};

const editOverrideCostPriceDisplay = (state: CompanySettingsState): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "overrideCostPriceDisplay",
    model: new RadioFieldModel<boolean>({
      value: state.response?.overrideCostPriceDisplay ?? false,
      idFn: String,
      labelFn: (v) =>
        v
          ? { key: "settings.override-cost-price-display.enabled" }
          : { key: "settings.override-cost-price-display.disabled" },
      options: [false, true],
    }),
  }),
];

const updateOverrideCostPriceDisplay = (orgNo: string, state: CompanySettingsState): DCEvent[] => {
  if (state.editField?.type === "overrideCostPriceDisplay") {
    return [
      settingsApi.updateCompanySettings({
        orgNo,
        data: { overrideCostPriceDisplay: state.editField.model.value },
        onSuccess: updateSettingsSuccess(orgNo),
        onFailure: () => [showGeneralError()],
      }),
    ];
  }
  return [];
};

const requestClientSystemAccess = (orgNo: string) => {
  return [
    settingsApi.requestClientSystemAccess({
      orgNo,
      onSuccess: (res) => [loadUrl(res.data.confirmUrl, false)],
      onFailure: () => [showGeneralError()],
    }),
  ];
};

const requestStandardSystemAccess = (orgNo: string) => {
  return [
    settingsApi.requestStandardSystemAccess({
      orgNo,
      onSuccess: (res) => [loadUrl(res.data.confirmUrl, false)],
      onFailure: () => [showGeneralError()],
    }),
  ];
};

const editShareholderContact = (orgNo: string, state: CompanySettingsState): DCEvent[] => {
  if (state.editField?.type === "editShareholderContact") {
    const isValid = [
      state.editField.name.validate(),
      state.editField.title.validate(),
      state.editField.email.validate(),
      state.editField.phoneNumber.validate(),
      state.editField.linkedin.validate(),
    ].every((valid) => valid);
    if (isValid) {
      const formData = new FormData();
      if (state.editField.uploadingImage) {
        formData.append("file", state.editField.uploadingImage);
      }
      if (state.editField.id) formData.set("id", state.editField.id);
      formData.set("name", state.editField.name.validatedValue);
      if (!state.editField.title.isEmpty)
        formData.set("title", state.editField.title.validatedValue);
      formData.set("email", state.editField.email.validatedValue);
      if (state.editField.phoneNumber.toValidPhoneNumber()) {
        formData.set("phoneNumber", state.editField.phoneNumber.number);
        formData.set(
          "phoneCountryCode",
          state.editField.phoneNumber.selectedCountry?.callingCode ?? "",
        );
      }
      if (!state.editField.linkedin.isEmpty)
        formData.set("linkedin", "https://" + state.editField.linkedin.validatedValue);
      if (state.editField.imageReference)
        formData.set("imageReference", state.editField.imageReference);

      return [
        setInCompanySettingsState("isLoading", true),
        settingsApi.changeShareholderContact({
          orgNo,
          data: formData,
          onSuccess: updateSettingsSuccess(orgNo),
          onFailure: () => [showGeneralError()],
        }),
      ];
    }
  }
  return [];
};

const showInviteAccountantDialog = (): DCEvent[] => [
  setInCompanySettingsState("isInviteAccountantDialogOpen", true),
];

const hideInviteAccountantDialog = (email: StringFieldModel): DCEvent[] => {
  email.clear();
  return [setInCompanySettingsState("isInviteAccountantDialogOpen", undefined)];
};

const submitInviteAccountantDialog = (
  orgNo: string,
  email: StringFieldModel,
  language: Language,
): DCEvent[] =>
  email.validate()
    ? [
        setInCompanySettingsState("isInviteAccountantDialogSubmitting", true),
        settingsApi.inviteAccountant({
          orgNo,
          email: email.inputValue,
          language,
          onSuccess: () => {
            email.clear();
            return [
              ...hideInviteAccountantDialog(email),
              setInCompanySettingsState("isInviteAccountantDialogSubmitting", undefined),
              showSuccess({ key: "invite-user.success-message", params: { count: 1 } }),
            ];
          },
          onFailure: () => [showGeneralError()],
        }),
      ]
    : [];

function openDeleteAccessDialog(user: User) {
  return [setInCompanySettingsState("showDeleteAccessDialogForUser", user)];
}

function hideDeleteAccessDialog() {
  return [setInCompanySettingsState("showDeleteAccessDialogForUser", undefined)];
}

function submitDeleteAccessDialog(orgNo: string, userId: number | undefined) {
  return userId
    ? [
        setInCompanySettingsState("isDeleteAccessDialogSubmitting", true),
        settingsApi.deleteAccountantAccess({
          orgNo,
          userId,
          onSuccess: () => {
            return [
              ...hideDeleteAccessDialog(),
              setInCompanySettingsState("isDeleteAccessDialogSubmitting", undefined),
              loadCompanySettings(orgNo),
              showGeneralSuccess(),
            ];
          },
          onFailure: () => [showGeneralError()],
        }),
      ]
    : [];
}

const deleteShareholderContact = (orgNo: string, id: string): DCEvent[] => [
  setInCompanySettingsState("isLoading", true),
  settingsApi.deleteShareholderContact({
    orgNo,
    id,
    onSuccess: updateSettingsSuccess(orgNo),
    onFailure: () => [showGeneralError()],
  }),
];

const prepSubscriptionContext = (
  company: Company,
  user: User,
  state: CompanySettingsState,
  enterpriseAccountInfo: EnterpriseAccountInfo | undefined,
): SubscriptionStatusContext => {
  const response = state.subscriptionResponse;

  return {
    subscriptionType: response?.subscriptionType,
    productDescription: response?.productDescription,
    currentPrice: response?.paidAmount,
    billInterval: response?.billInterval,
    companyName: company.name,
    orgNo: company.organizationnumber,
    email: user.email ?? "",
    name: formatUserName(user),
    tier: toTier(response),
    productName: response?.productName,
    paymentMethod: response?.paymentMethod,
    paymentDetails: response?.paymentDetails,
    enterpriseAccountInfo: enterpriseAccountInfo,
    availableEnterpriseAccounts: state.enterpriseAccountsResponse?.enterpriseAccounts ?? [],
  };
};

const prepUsers = (
  dispatch: EventDispatcher,
  orgNo: string,
  roles: CustomRoleItem[],
  selectedFilter: UsersByRole,
  usersWithAdminAccess?: AdminItem[],
): UserListItem[] => {
  const filterType = selectedFilter.type;
  const admins =
    filterType === "all_roles" || filterType === "administrators"
      ? orderBy(usersWithAdminAccess, (admin) => admin.name).map<UserListItem>((admin) => ({
          key: admin.id,
          name: admin.name,
          email: admin.email,
          lastLogin: admin.lastSeen,
          tagLabel: { color: "info", label: { key: "accessType.ADMIN" } },
          onRevoke: () => dispatch(onRevokeAdminAccess(orgNo, admin)),
          revokeQuestion: {
            key: "settings.revokeAdminAccessForQuestion",
            params: { name: admin.name },
          },
        }))
      : [];

  const usersWithRoles: UserListItem[] = roles
    .filter(
      (c) =>
        filterType === "all_roles" ||
        (selectedFilter.type === "custom_role" &&
          selectedFilter.role?.customRoleId === c.role.customRoleId),
    )
    .map((c) => {
      return c.availableUsers.map<UserListItem>((user) => ({
        key: user.externalId,
        name: user.name,
        lastLogin: user.lastLogin,
        email: user.email ?? "",
        tagLabel: { color: "info", label: { translated: c.role.customRoleName } },
        onRevoke: () =>
          dispatch(revokeAccessToRole({ orgNo, user, customRoleId: c.role.customRoleId })),
        revokeQuestion: {
          key: "custom-roles.revokeQuestion",
          params: { name: user.name, role: c.role.customRoleName },
        },
      }));
    })
    .flatMap((c) => c);

  return [...admins, ...usersWithRoles];
};

export const allRoles: UsersByRole = {
  type: "all_roles",
  label: { key: "all-roles" },
};

export const administratorRole: UsersByRole = {
  type: "administrators",
  label: { key: "administrators" },
};

export const createFilterOptionByRole = (roleItem: CustomRoleItem): UsersByRole => ({
  type: "custom_role",
  role: roleItem.role,
  label: { translated: roleItem.role.customRoleName },
});

const createUsersByRoleModel = (
  dispatch: EventDispatcher,
  roles: CustomRoleItem[],
  filter: UsersByRole,
): RadioFieldModel<UsersByRole> => {
  const value = filter;
  const customRoles: UsersByRole[] = roles.map(createFilterOptionByRole);
  return new RadioFieldModel<UsersByRole>({
    value,
    idFn: (o) => o.type + o.role?.customRoleId,
    labelFn: (o) => o.label,
    options: [allRoles, administratorRole, ...customRoles],
    onChange: (o) => dispatch([setInCompanySettingsState("selectedUserRoleFilter", o)]),
  });
};

const isAllFeaturesManageOrEdit = (
  moduleName: ModuleName,
  state: CompanySettingsState,
): boolean => {
  if (state.editField?.type === "roleDialog") {
    const featureAccesses = state.editField.featureAccesses;
    const m = state.response?.modules.find((m) => m.moduleName === moduleName);
    return (
      (m &&
        m.features.every((f) => {
          if (f.isManageable) {
            return featureAccesses[f.featureName].value === "MANAGE";
          } else if (f.isEditable) {
            return featureAccesses[f.featureName].value === "EDIT";
          } else {
            return featureAccesses[f.featureName].value === "VIEW";
          }
        })) ??
      false
    );
  } else {
    return false;
  }
};

export const previewCustomRole = (role: CustomRole, orgNo: string): DCEvent[] => {
  const roleId = role.customRoleId;
  const baseUrl = findInitialUrl({ orgNo, role });
  return [
    settingsApi.previewCustomRole({
      orgNo,
      roleId,
      onSuccess: () =>
        compact([
          baseUrl &&
            loadUrl(
              customRoleCompanyUrl({
                roleId,
                baseUrl,
              }),
            ),
        ]),
      onFailure: () => [showGeneralError()],
    }),
  ];
};

const isFeatureToggledOn = (feature: FeatureName, featureToggles: FeatureToggles): boolean => {
  switch (feature) {
    case FeatureName.capTable:
    case FeatureName.financialInstruments:
      return Boolean(featureToggles.financialInstrumentOptions);
    case FeatureName.commitments:
      return Boolean(featureToggles.committedCapital);
    default:
      return true;
  }
};

export const openNewOrEditRoleDialog = (
  portfolios: Portfolio[],
  portfolioCompanies: PortfolioCompany[],
  company: Company,
  state: CompanySettingsState,
  dispatch: EventDispatcher,
  featureToggles: FeatureToggles,
  paidSubscription?: PaidSubscription,
  roleItem?: CustomRoleItem,
): DCEvent[] => {
  const orgNo = company.organizationnumber;
  const data = roleItem?.role;
  const response = state.response;
  if (!response) {
    return [];
  }

  const featureAccesses: { [key in FeatureName]: RadioFieldModel<FeatureAccess> } =
    Object.fromEntries(
      response.modules
        .flatMap((m) => m.features)
        .map((f) => [
          [f.featureName],
          new RadioFieldModel({
            value:
              data?.features.find((feature) => feature.featureName === f.featureName)?.access ??
              "NONE",
            idFn: identity,
            options: Object.values(FeatureAccessValues).filter((a) =>
              a === "MANAGE"
                ? f.showManage
                : a === "EDIT"
                  ? f.showEdit
                  : a === "NONE"
                    ? f.isHideable
                    : true,
            ),
            labelFn: (a) => (a ? featureAccessLabels[a as FeatureAccess] : { translated: "" }),
            disabledFn: (a) =>
              a === "MANAGE"
                ? !f.isManageable
                : a === "EDIT"
                  ? !f.isEditable
                  : a === "VIEW"
                    ? !f.isViewable
                    : a === "NONE"
                      ? !f.isHideable
                      : true,
          }),
        ]),
    );
  const defaultLanguageModel = new CustomRoleLanguageModel(
    defaultLanguageForOrgNo(orgNo),
    orgNo,
    data,
  );

  const selectedCompaniesUsingRole: CompanyItem[] = roleItem?.companies ?? [
    { organisationNumber: company.organizationnumber, companyName: company.name },
  ];
  const usersUsingRole = roleItem?.users ?? [];
  const companiesRoleSelected = CompaniesRoleSelected({
    selectedOrgNos: selectedCompaniesUsingRole.map((c) => c.organisationNumber),
    filteredCompanies: portfolioCompanies,
    usersUsingRole,
    orgNo: company.organizationnumber,
  });
  if (!roleItem) {
    companiesRoleSelected.selectAll();
  }
  return [
    setInCompanySettingsState("selectedCompaniesUsingRole", selectedCompaniesUsingRole),
    setInCompanySettingsState("editField", {
      type: "roleDialog",
      data,
      companiesUsingRole: roleItem?.companies ?? [],
      usersUsingRole,
      companyName: company.name,
      roleLanguageModels: [
        defaultLanguageModel,
        new CustomRoleLanguageModel(Language.EN, orgNo, data),
      ],
      showDescription: data?.customRoleLanguages.some((l) => isNotBlank(l.description)) ?? false,
      filteredModulesBySubscription: response.modules
        .filter((module) => paidSubscription?.allowsDbot || module.moduleName !== ModuleName.DBOT)
        .map((module) => ({
          ...module,
          features: module.features.filter((f) =>
            isFeatureToggledOn(f.featureName, featureToggles),
          ),
        })),
      enabledModules: data?.modules ?? [],
      expandedModules: data?.modules?.slice() ?? [],
      modules: response.modules,
      featureUnavailableInfoLabel: isSwedishOrgNo(orgNo)
        ? "feature.unavailable-info-swedish-company"
        : "feature.unavailable-info-norwegian-company",
      availableFeaturesBasedOnCountry: Object.fromEntries(
        Object.values(FeatureName).map<[FeatureName, boolean]>((feature) => [
          feature,
          isNorwegianOrgNo(orgNo) ||
            ![FeatureName.shareholderRegisterStatement, FeatureName.beneficialOwners].includes(
              feature,
            ),
        ]),
      ) as { [key in FeatureName]: boolean },
      featureAccesses,
      roleSharing: createSharingModel(roleItem),
      toggleViewEditRoleSelection: [toggleViewEditRoleSelectionDialog()],
      updateSelectedCompaniesUsingRole: (selectedCompanies) => {
        const companiesWithUsers = [...new Set(usersUsingRole.map((a) => a.orgNo))].filter(
          (a) => a !== undefined,
        );
        const addCompaniesWithUsersToSelected = companiesWithUsers.filter(
          (orgNo) => !selectedCompanies.includes(orgNo) && orgNo !== company.organizationnumber,
        );
        const allOrgNos = [
          company.organizationnumber,
          ...addCompaniesWithUsersToSelected,
          ...selectedCompanies,
        ];
        const portfolioByOrgNo = new Map(portfolioCompanies.map((c) => [c.organisationNumber, c]));
        const companyItems: CompanyItem[] = allOrgNos.map((orgNo) => {
          const pc = portfolioByOrgNo.get(orgNo);
          return { organisationNumber: orgNo, companyName: pc?.name ?? orgNo };
        });
        dispatch([
          setInCompanySettingsState("selectedCompaniesUsingRole", companyItems),
          toggleViewEditRoleSelectionDialog(),
        ]);
      },
      showViewEditRoleSelection: false,
      companiesRoleSelected,
      portfolioCompaniesSelectItem: portfolioCompaniesSelectProps({
        companies: portfolioCompanies,
        companiesRoleSelected,
        primaryOrgNo: company.organizationnumber,
        usersUsingRole,
        dispatch,
      }),
    }),
  ];
};

const portfolioCompaniesSelectProps = ({
  companies,
  companiesRoleSelected,
  primaryOrgNo,
  usersUsingRole,
  dispatch,
}: {
  companies: PortfolioCompany[];
  companiesRoleSelected: OptionFieldModel<PortfolioCompany>;
  primaryOrgNo: string;
  usersUsingRole: UserItem[];
  dispatch: EventDispatcher;
}): PortfolioCompaniesSelectProps => {
  const hasUsersInOrgNo = (orgNo: string) => usersUsingRole.some((u) => u.orgNo === orgNo);
  return {
    companies,
    companySearchFieldModel: new StringFieldModel(),
    isContextCustomRole: true,
    get selectedCompaniesCount() {
      return companiesRoleSelected.selectedIds.size;
    },
    action: {
      isCompanySelected: (orgNo) =>
        orgNo === primaryOrgNo || companiesRoleSelected.selectedIds.has(orgNo),
      isCompanyDisabled: (orgNo) => orgNo === primaryOrgNo || hasUsersInOrgNo(orgNo),
      toggleCompanyInAddDialog: (orgNo) => {
        if (orgNo === primaryOrgNo || hasUsersInOrgNo(orgNo)) return;
        const company = companies.find((c) => c.organisationNumber === orgNo);
        if (company) companiesRoleSelected.toggle(company);
      },
      toggleAllCompanies: () => companiesRoleSelected.selectAll(),
      deselectAllCompanies: () => companiesRoleSelected.clear(),
      filterSelectedCompanies: () =>
        dispatch([
          setInCompanySettingsState("editField", (current) => {
            if (current.editField?.type === "roleDialog") {
              const item = current.editField.portfolioCompaniesSelectItem;
              item.showSelectedAll = !item.showSelectedAll;
            }
            return current.editField;
          }),
        ]),
    },
  };
};

export const otherCompaniesSharingRole = (companies: CompanyItem[], orgNo: string) =>
  companies.filter((c) => c.organisationNumber !== orgNo);

export const roleSharingLabel = (isShared: boolean, companyName: string): TMessage =>
  isShared
    ? { key: "roles.sharing.shared" }
    : { key: "roles.sharing.private", params: { companyName } };

export const toggleViewEditRoleSelectionDialog = (): DCEvent => {
  return setInCompanySettingsState("editField", (_, currentValue) => {
    if (currentValue?.type === "roleDialog") {
      currentValue.showViewEditRoleSelection = !currentValue.showViewEditRoleSelection;
    }
    return currentValue;
  });
};

const clearLoadingAndShowGeneralError = (): DCEvent[] => [
  setInCompanySettingsState("isLoading", undefined),
  showGeneralError(),
];

export const deleteCustomRole = (orgNo: string, role: CustomRole): DCEvent[] => [
  setInCompanySettingsState("isLoading", true),
  settingsApi.deleteCustomRole({
    orgNo,
    roleId: role.customRoleId,
    onSuccess: () => [
      setInCompanySettingsState("isLoading", undefined),
      loadCompanySettings(orgNo),
      showGeneralSuccess(),
    ],
    onFailure: clearLoadingAndShowGeneralError,
  }),
];

const toggleRoleDescription = (state: CompanySettingsState): DCEvent[] => {
  if (state.editField?.type === "roleDialog") {
    if (state.editField.showDescription) {
      state.editField.roleLanguageModels.forEach((l) => l.description.setAndCommitInputValue(""));
    }
    return [
      setInCompanySettingsState("editField", {
        ...state.editField,
        showDescription: !state.editField.showDescription,
      }),
    ];
  } else {
    return [];
  }
};

export const toggleModule = (
  moduleName: ModuleName,
  flag: boolean,
  state: CompanySettingsState,
): DCEvent[] => {
  if (state.editField?.type === "roleDialog") {
    const model = state.editField;
    const isExpanded = model.expandedModules.includes(moduleName);
    if (flag) {
      model.enabledModules.push(moduleName);
      resetToAllAccess(moduleName, state);
    } else {
      model.enabledModules = model.enabledModules.filter((m) => m !== moduleName);
    }
    if (flag !== isExpanded) {
      return toggleExpandModule(moduleName, state);
    }
    return [];
  } else {
    return [];
  }
};

export const toggleExpandModule = (
  moduleName: ModuleName,
  state: CompanySettingsState,
): DCEvent[] => {
  if (state.editField?.type === "roleDialog") {
    return [
      setInCompanySettingsState("editField", {
        ...state.editField,
        expandedModules: toggleElement(state.editField.expandedModules, moduleName),
      }),
      setInCompanySettingsState("validationError", undefined),
    ];
  } else {
    return [];
  }
};

export const resetToAllAccess = (
  moduleName: ModuleName,
  state: CompanySettingsState,
): DCEvent[] => {
  const model = state.editField;
  if (model?.type === "roleDialog") {
    const productModule = model.modules.find((m) => m.moduleName === moduleName)!;
    productModule?.features.forEach((f) => {
      if (f.isManageable && f.showManage) {
        model.featureAccesses[f.featureName].setValue("MANAGE");
      } else if (f.isEditable && f.showEdit) {
        model.featureAccesses[f.featureName].setValue("EDIT");
      } else if (f.isViewable) {
        model.featureAccesses[f.featureName].setValue("VIEW");
      }
    });
  }
  return [];
};

const updateSettingsSuccess =
  (orgNo: string): ((res: HttpResponse, state: RootStoreData) => DCEvent[]) =>
  () => [
    setInCompanySettingsState("isLoading", undefined),
    setInCompanySettingsState("editField", undefined),
    setInCompanySettingsState("validationError", undefined),
    showGeneralSuccess(),
    loadCompanySettings(orgNo),
  ];

export const saveRole = (state: CompanySettingsState, orgNo: string): DCEvent[] => {
  if (state.editField?.type === "roleDialog") {
    const onSuccess = updateSettingsSuccess(orgNo);
    const onFailure: (err: unknown, state: RootStoreData) => DCEvent[] = () => [showGeneralError()];
    const data = createRequestData(state.editField, orgNo, state.selectedCompaniesUsingRole);
    if (!data.enabledModules.length || data.enabledModules.length < 1)
      return [
        setInCompanySettingsState("editField", {
          ...state.editField,
        }),
        setInCompanySettingsState("validationError", {
          key: "roles.validate-message-no-module-choose",
        }),
      ];
    if ("customRoleId" in data && data.customRoleId !== "template") {
      return [settingsApi.updateCustomRole({ data, orgNo, onSuccess, onFailure })];
    } else {
      const callApi = (request: NewCustomRoleRequest): DCEvent =>
        settingsApi.newCustomRole({
          data: request,
          orgNo,
          onSuccess,
          onFailure,
        });

      if ("customRoleId" in data) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { customRoleId, ...rest } = data;
        return [callApi(rest)];
      } else {
        return [callApi(data)];
      }
    }
  }
  return [];
};

const createRequestData = (
  model: RoleDialogModel,
  orgNo: string,
  selectedCompaniesUsingRole: CompanyItem[],
): NewCustomRoleRequest | UpdateCustomRoleRequest => {
  const defaultLanguage = defaultLanguageForOrgNo(orgNo);
  const featureAccessList: AccessToFeature[] = Object.entries(model.featureAccesses)
    .map<AccessToFeature>(([featureName, model]) => ({
      featureName: FeatureName[featureName as keyof typeof FeatureName],
      access: model.value ?? "NONE",
    }))
    .filter((f) => f.access !== "NONE");
  const index = model.roleLanguageModels.findIndex(
    (roleLanguage) => defaultLanguage === roleLanguage.language,
  );
  const request: NewCustomRoleRequest = {
    roleName: model.roleLanguageModels[index].roleName.validatedValue,
    roleLanguages: model.roleLanguageModels
      .filter((roleLanguageModel) => isNotBlank(roleLanguageModel.roleName.validatedValue))
      .map((defaultRoleLanguageModel) => {
        return {
          customRoleId:
            model.data?.customRoleId && model.data?.customRoleId !== "template"
              ? model.data.customRoleId
              : newUuid(),
          language: defaultRoleLanguageModel.language,
          name: defaultRoleLanguageModel.roleName.validatedValue,
          description: defaultRoleLanguageModel.description?.validatedValue,
        };
      }),
    enabledModules: model.enabledModules ?? [],
    isShared: model.roleSharing.value,
    companiesUsingRole: compact([
      ...selectedCompaniesUsingRole.map((c) => c.organisationNumber),
      model.data?.orgNo,
    ]),
    featureAccessList,
  };
  if (model.data) {
    return {
      ...request,
      customRoleId: model.data.customRoleId,
    };
  } else {
    return request;
  }
};

export const revokeAccessToRole = ({
  orgNo,
  user,
  customRoleId,
  onSuccess,
}: {
  orgNo: string;
  user: UserItem;
  customRoleId: string;
  onSuccess?: () => DCEvent[];
}): DCEvent[] => [
  settingsApi.revokeAccessToRole({
    orgNo,
    userId: user.externalId,
    customRoleId,
    onSuccess: onSuccess
      ? onSuccess
      : () => [showGeneralSuccess(), loadCompanySettings(orgNo), loadUsersWithAdminAccess(orgNo)],
    onFailure: () => [showGeneralError()],
  }),
];

export const previousYear = new Date().getFullYear() - 1;

export const yearOptions = Array(10)
  .fill(0)
  .map((_, i) => (previousYear - i).toString());

export const currencyModel = (currency: string, onChange?: (currency: string) => void) =>
  new RadioFieldModel({
    value: currency,
    options: ["NOK", "EUR", "SEK", "DKK", "USD"],
    idFn: (item) => item,
    labelFn: (item) => ({ translated: item }),
    onChange,
  });

export const onAddNewTaxAssessment = (currency: string) => [
  setInCompanySettingsState("editField", {
    type: "taxAssessmentValue",
    yearOptions,
    data: {
      assessmentvalueId: "new",
      assessmentValue: { currency, value: 0 },
      year: previousYear,
    },
    yearModel: new YearFieldModel({ value: previousYear.toString() }),
    assessmentValue: new NumberFieldModel({ isPositiveOrZero: true }),
    currency: currencyModel(currency),
  }),
];

export const onEditTaxAssessment = (data: TaxAssessmentValue): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "taxAssessmentValue",
    assessmentValue: new NumberFieldModel({
      value: data.assessmentValue.value,
      isPositiveOrZero: true,
    }),
    yearModel: new YearFieldModel({ value: data.year.toString() }),
    data,
    yearOptions,
    currency: currencyModel(data.assessmentValue.currency),
  }),
];

export const deleteTaxAssessmentValue = (
  orgNo: string,
  taxAssessmentValueId: string,
): DCEvent[] => [
  settingsApi.deleteTaxAssessmentValue({
    orgNo,
    taxAssessmentValueId,
    onSuccess: () => [loadCompanySettings(orgNo), showGeneralSuccess()],
    onFailure: () => [showGeneralError()],
  }),
];

export const saveTaxAssessmentValue = (
  orgNo: string,
  currency: string,
  state: CompanySettingsState,
): DCEvent[] => {
  const e = state.editField;
  if (e?.type === "taxAssessmentValue") {
    if (e.yearModel?.validate() && e.assessmentValue?.validate()) {
      const isNew = e.data?.assessmentvalueId === "new";
      if (isNew) {
        return [
          settingsApi.addNewTaxAssessmentValue({
            orgNo,
            taxAssessmentValue: {
              value: e.assessmentValue?.toNumberOrZero(),
              currency: e.currency?.value ?? currency,
            },
            year: e.yearModel?.toNumberOrZero(),
            onSuccess: () => [
              loadCompanySettings(orgNo),
              setInCompanySettingsState("editField", undefined),
              showGeneralSuccess(),
            ],
            onFailure: () => [showGeneralError()],
          }),
        ];
      } else {
        const data: TaxAssessmentValue = {
          ...e.data!,
          assessmentValue: {
            currency: e.currency?.value ?? currency,
            value: e.assessmentValue?.toNumberOrZero(),
          },
          year: e.yearModel?.toNumberOrZero(),
        };
        return [
          settingsApi.updateTaxAssessmentValue({
            orgNo,
            data,
            onSuccess: () => [
              loadCompanySettings(orgNo),
              setInCompanySettingsState("editField", undefined),
              showGeneralSuccess(),
            ],
            onFailure: () => [showGeneralError()],
          }),
        ];
      }
    }
    return [];
  } else {
    return [];
  }
};

//Company update info stuff
export const editCompanyName = (currentValue: string, mustEndWithAs = false): DCEvent[] => {
  return [
    setInCompanySettingsState("editField", {
      type: "companyName",
      currentValue,
      updateModel: new StringFieldModel({
        required: true,
        value: currentValue,
        ...(mustEndWithAs ? { validator: validateEndsWithAs } : {}),
      }),
      effectiveFromModel: new LocalDateFieldModel({ maxDate: toLocalDateISOString(Date.now()) }),
    }),
  ];
};

const validateEndsWithAs = (value: string): TMessage | undefined =>
  value.trim().endsWith("AS") ? undefined : { key: "settings.updateCompanyName.mustEndWithAs" };

export const companyAddresses = (
  response?: GetCompanySettingsResponse,
): Record<AddressType, CompanyAddress> => ({
  BUSINESS: {
    addressLine1: response?.company.businessAddress1,
    addressLine2: response?.company.businessAddress2,
    postalCode: response?.company.businessZip,
    postalPlace: response?.company.businessCity,
    country: response?.company.businessCountry,
  },
  POSTAL: {
    addressLine1: response?.company.mailingAddress1,
    addressLine2: response?.company.mailingAddress2,
    postalCode: response?.company.mailingZip,
    postalPlace: response?.company.mailingCity,
    country: response?.company.mailingCountry,
  },
});

const addressRows = (address: CompanyAddress, locale: Language): AddressCardRow[] => [
  {
    id: "addressLine1",
    label: { key: "settings.company.addressLine1" },
    value: address.addressLine1 ?? "",
  },
  {
    id: "addressLine2",
    label: { key: "settings.company.addressLine2" },
    value: address.addressLine2 ?? "",
  },
  {
    id: "postalCode",
    label: { key: "settings.updateCompanyPostalCode" },
    value: address.postalCode ?? "",
  },
  {
    id: "postalPlace",
    label: { key: "settings.updateCompanyPostalPlace" },
    value: address.postalPlace ?? "",
  },
  {
    id: "country",
    label: { key: "settings.updateCompanyCountry" },
    value: getCountryName(address.country ?? "", locale) ?? "",
  },
];

export const addressCards = (
  response: GetCompanySettingsResponse | undefined,
  locale: Language,
): AddressCard[] => {
  const addresses = companyAddresses(response);
  return [
    {
      addressType: "BUSINESS",
      effectiveFrom: response?.businessAddressEffectiveFrom,
      rows: addressRows(addresses.BUSINESS, locale),
    },
    {
      addressType: "POSTAL",
      effectiveFrom: response?.postalAddressEffectiveFrom,
      rows: addressRows(addresses.POSTAL, locale),
    },
  ];
};

export const editAddress = (
  addressType: AddressType,
  address: CompanyAddress,
  locale: Language,
): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "address",
    form: newAddressForm(addressType, address, locale),
  }),
];

export const saveAddress = (orgNo: string, state: CompanySettingsState): DCEvent[] => {
  if (state.editField?.type !== "address") {
    return [];
  }
  const { addressType } = state.editField.form;
  return [
    settingsApi.updateAddress({
      orgNo,
      request: toUpdateAddressRequest(state.editField.form),
      onSuccess: () => [
        setInCompanySettingsState("editField", undefined),
        showSuccess(addressUpdatedMessage(addressType), { autoHide: true }),
        loadCompanySettings(orgNo),
      ],
      onFailure: () => [showGeneralError()],
    }),
  ];
};

export const onEditAuditor = ({
  dispatch,
  orgNo,
  currentAuditor,
}: {
  dispatch: EventDispatcher;
  orgNo: string;
  currentAuditor?: AuditorListItem;
}): DCEvent[] => {
  const companySearchModel = new StringFieldModel({
    value: currentAuditor
      ? `${currentAuditor.name} (${formatOrgNum(currentAuditor.organisationNumber)})`
      : "",
  });
  return [
    setInCompanySettingsState("isLoadingAuditor", false),
    loadCompanySettings(orgNo),
    setInCompanySettingsState("editField", {
      type: "auditor",
      updateModel: {
        label: { key: "settings.selectAuditor" },
        items: [],
        idFn: (item) => item?.organizationnumber ?? "",
        labelFn: (item) => item?.name ?? "",
        onSelect: (item) => dispatch(onSelectAuditor(item)),
        onInputValueChange: (query) => {
          companySearchModel.setInputValue(query);
          if (query.length >= 2) {
            debouncedCompanySearch(dispatch, query);
          }
        },
        onClear: () => dispatch(onClearAuditor()),
      },
      companySearchModel,
    }),
  ];
};

export const onEditAccountant = ({
  dispatch,
  currentAccountant,
}: {
  dispatch: EventDispatcher;
  orgNo: string;
  currentAccountant?: NamedOrganisationNumber;
}): DCEvent[] => {
  const companySearchModel = new StringFieldModel({
    value: currentAccountant
      ? `${currentAccountant.name} (${formatOrgNum(currentAccountant.orgNo)})`
      : "",
  });
  return [
    setInCompanySettingsState("editField", {
      type: "accountant",
      companySearchModel,
      updateModel: {
        label: { key: "settings.selectAccountant" },
        items: [],
        idFn: (item) => item?.organizationnumber ?? "",
        labelFn: (item) => item?.name ?? "",
        onSelect: (item) => dispatch(onSelectAccountant(item)),
        onClear: () => dispatch(onClearAccountant()),
        onInputValueChange: (query) => {
          companySearchModel.setInputValue(query);
          if (query.length >= 2) {
            debouncedCompanySearch(dispatch, query);
          }
        },
      },
    }),
  ];
};

const debouncedCompanySearch = debounce(
  (dispatch: EventDispatcher, query: string) =>
    dispatch([
      settingsApi.searchCompanies({
        query,
        onSuccess: (r) => onSearchCompanySuccess(r.data),
        onFailure: () => [showGeneralError()],
      }),
    ]),
  300,
);

const onSelectAuditor = (item?: SuggestedCompany): DCEvent[] => {
  if (!item) return [];
  return [
    setInCompanySettingsState("editField", (c) => {
      if (c.editField?.type === "auditor") {
        c.editField.companySearchModel.setInputValue(item.name);
        return {
          ...c.editField,
          updateModel: {
            ...c.editField.updateModel,
            value: item,
          },
        };
      }
    }),
  ];
};

const onSearchCompanySuccess = (companies?: SuggestedCompany[]): DCEvent[] => {
  if (!companies) return [];
  return [
    setInCompanySettingsState("editField", (c) => {
      if (c.editField?.type === "accountant") {
        return {
          ...c.editField,
          updateModel: {
            ...c.editField.updateModel,
            items: companies,
          },
        };
      } else if (c.editField?.type === "auditor") {
        return {
          ...c.editField,
          updateModel: {
            ...c.editField.updateModel,
            items: companies,
          },
        };
      }
    }),
  ];
};

const onSelectAccountant = (item?: SuggestedCompany): DCEvent[] => {
  if (!item) return [];
  return [
    setInCompanySettingsState("editField", (c) => {
      if (c.editField?.type === "accountant") {
        c.editField.companySearchModel.setInputValue(item?.name);
        return {
          ...c.editField,
          updateModel: {
            ...c.editField.updateModel,
            value: item,
          },
        };
      }
    }),
  ];
};

const onClearAuditor = (): DCEvent[] => {
  return [
    setInCompanySettingsState("editField", (c) => {
      if (c.editField?.type === "auditor") {
        c.editField.companySearchModel.setInputValue("");
        return {
          ...c.editField,
          updateModel: {
            ...c.editField.updateModel,
            items: [],
          },
        };
      }
    }),
  ];
};

const onClearAccountant = (): DCEvent[] => {
  return [
    setInCompanySettingsState("editField", (c) => {
      if (c.editField?.type === "accountant") {
        c.editField.companySearchModel.setInputValue("");
        return {
          ...c.editField,
          updateModel: {
            ...c.editField.updateModel,
            items: [],
          },
        };
      }
    }),
  ];
};

const updateAuditor = (orgNo: string, editField?: CompanySettingsEditableField): DCEvent[] => {
  if (editField && editField.type !== "auditor") {
    return [];
  }

  return [
    setInCompanySettingsState("isLoadingAuditor", true),
    settingsApi.updateCurrentAuditor({
      orgNo,
      auditorOrgNo: editField?.updateModel.value?.organizationnumber ?? "",
      onSuccess: () => [
        setInCompanySettingsState("isLoadingAuditor", false),
        setInCompanySettingsState("editField", undefined),
        showGeneralSuccess(),
        loadCompanySettings(orgNo),
      ],
      onFailure: () => [setInCompanySettingsState("isLoadingAuditor", false), showGeneralError()],
    }),
  ];
};

const updateAccountant = (orgNo: string, editField?: CompanySettingsEditableField): DCEvent[] => {
  if (editField && editField.type !== "accountant") {
    return [];
  }

  return [
    setInCompanySettingsState("isLoadingAccountant", true),
    settingsApi.updateCurrentAccountant({
      orgNo,
      accountantOrgNo: editField?.updateModel.value?.organizationnumber ?? "",
      onSuccess: () => [
        setInCompanySettingsState("isLoadingAccountant", false),
        setInCompanySettingsState("editField", undefined),
        showGeneralSuccess(),
        loadCompanySettings(orgNo),
      ],
      onFailure: () => [
        setInCompanySettingsState("isLoadingAccountant", false),
        showGeneralError(),
      ],
    }),
  ];
};

export const onSaveCompanyField = ({
  orgNo,
  data,
}: {
  orgNo: string;
  data?: UpdateCompanyInfoRequest;
}): DCEvent[] => {
  if (!data) {
    return [];
  }
  return [
    setInCompanySettingsState("isLoading", true),
    settingsApi.updateCompanyInfo({
      orgNo,
      data: data,
      onSuccess: () =>
        compact([
          loadCompanySettings(orgNo),
          data.newCompanyName !== undefined && ({ type: "update-company-info" } as const),
          setInCompanySettingsState("isLoading", undefined),
          setInCompanySettingsState("editField", undefined),
          showGeneralSuccess(),
        ]),
      onFailure: () => [
        setInCompanySettingsState("isLoading", undefined),
        setInCompanySettingsState("editField", undefined),
        showGeneralError(),
      ],
    }),
  ];
};

export const confirmOnboardingCompanySettings = (orgNo: string): DCEvent[] => {
  return [
    settingsApi.onboardCompanySettings({
      orgNo,
      onSuccess: () => [
        setInCompanySettingsState("isLoading", undefined),
        setInCompanySettingsState("onboardStatusResult", undefined),
        showGeneralSuccess(),
      ],
      onFailure: () => [showGeneralError()],
    }),
  ];
};

export const onEditFairValue = (
  currency: string,
  shareTypes: ShareType[],
  data?: FairValue,
): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "fairValue",
    currency,
    fairValueId: data?.fairValueId ?? "new",
    fairValueDate: new LocalDateFieldModel({
      value: data?.fairValueDate || toLocalDateISOString(Date.now()),
      required: true,
    }),
    shareType: new RadioFieldModel({
      value: shareTypes.find((shareType) => shareType.sharetypeId === data?.shareTypeId) ?? null,
      options: data !== undefined ? shareTypes : [null, ...shareTypes],
      idFn: (shareType) => shareType?.sharetypeId.toString() ?? "ALL",
      labelFn: () => ({
        translated: "",
      }),
    }),
    fairValue: new NumberFieldModel({
      value: data?.fairValue?.value,
      isPositiveOrZero: true,
      required: true,
    }),
  }),
];

export const saveFairValue = (
  orgNo: string,
  currency: string,
  state: CompanySettingsState,
): DCEvent[] => {
  const e = state.editField;
  if (e?.type === "fairValue") {
    if (e.fairValue?.validate() && e.fairValue?.validate() && e.shareType.validate()) {
      const isNew = e.fairValueId === "new";
      if (isNew) {
        return [
          settingsApi.addNewFairValue({
            orgNo,
            fairValueDate: e.fairValueDate.validatedValue!,
            fairValue: { value: e.fairValue.toNumberOrZero(), currency },
            shareTypeId: e.shareType.value?.sharetypeId,
            onSuccess: () => [
              loadCompanySettings(orgNo),
              setInCompanySettingsState("editField", undefined),
              showGeneralSuccess(),
            ],
            onFailure: () => [showGeneralError()],
          }),
        ];
      } else {
        const data: FairValue = {
          fairValueId: e.fairValueId,
          fairValue: {
            currency,
            value: e.fairValue?.toNumberOrZero(),
          },
          fairValueDate: e.fairValueDate.validatedValue!,
          shareTypeId: e.shareType.value!.sharetypeId,
        };
        return [
          settingsApi.updateFairValue({
            orgNo,
            data,
            onSuccess: () => [
              loadCompanySettings(orgNo),
              setInCompanySettingsState("editField", undefined),
              showGeneralSuccess(),
            ],
            onFailure: () => [showGeneralError()],
          }),
        ];
      }
    } else {
      logDebug("Invalid form");
      return [];
    }
  } else {
    return [];
  }
};

export const onDeleteFairValue = (orgNo: string, fairValueId: string): DCEvent[] => [
  settingsApi.deleteFairValue({
    orgNo,
    fairValueId,
    onSuccess: () => [loadCompanySettings(orgNo), showGeneralSuccess()],
    onFailure: () => [showGeneralError()],
  }),
];

//Branding stuff
export const onEditDocumentLanguage = (orgNo: string, documentLanguage?: Language[]): DCEvent[] => {
  return [
    setInCompanySettingsState("editField", {
      type: "documentLanguage",
      updateModel: documentLanguageFieldModel({
        orgNo,
        value: mapLocalesToDocumentLanguage(documentLanguage),
      }),
    }),
  ];
};

export const onSaveDocumentLanguage = (
  orgNo: string,
  documentLanguage?: DocumentLanguage,
): DCEvent[] => {
  if (!documentLanguage) {
    return [];
  }
  return [
    setInCompanySettingsState("isLoading", true),
    settingsApi.updateDocumentLanguage({
      orgNo,
      data: { defaultDocumentLanguages: mapDocumentLanguageToLocales(documentLanguage) },
      onSuccess: () => [
        loadCompanySettings(orgNo),
        setInCompanySettingsState("isLoading", undefined),
        setInCompanySettingsState("editField", undefined),
        showGeneralSuccess(),
      ],
      onFailure: () => [
        setInCompanySettingsState("isLoading", undefined),
        setInCompanySettingsState("editField", undefined),
        showGeneralError(),
      ],
    }),
  ];
};

export const onEditDocumentFont = (
  supportedFonts: DocumentFont[],
  currentValue?: DocumentFont,
): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "documentFont",
    updateModel: new RadioFieldModel<DocumentFont>({
      value: currentValue ?? supportedFonts[0],
      options: supportedFonts,
      idFn: (option) => option.value,
      labelFn: (option) => ({ translated: option.displayName }),
    }),
  }),
];

export const onSaveDocumentFont = (orgNo: string, font: DocumentFont): DCEvent[] => {
  return [
    setInCompanySettingsState("isLoading", true),
    settingsApi.updateDocumentFont({
      orgNo,
      data: { font },
      onSuccess: () => [
        loadCompanySettings(orgNo),
        setInCompanySettingsState("isLoading", undefined),
        setInCompanySettingsState("editField", undefined),
        showGeneralSuccess(),
      ],
      onFailure: () => [
        setInCompanySettingsState("isLoading", undefined),
        setInCompanySettingsState("editField", undefined),
        showGeneralError(),
      ],
    }),
  ];
};

export const onEditLogoPlacement = (logoPlacementOption: LogoPlacementOption): DCEvent[] => {
  return [
    setInCompanySettingsState("editField", {
      type: "documentLogoPlacement",
      updateModel: new RadioFieldModel<LogoPlacementOption>({
        value: logoPlacementOption,
        options: ["center", "left", "right"],
        idFn: (option) => String(option),
        labelFn: (option) => ({ key: option }),
      }),
    }),
  ];
};

export const onSaveDocumentPlacement = (
  orgNo: string,
  logoPlacement: LogoPlacementOption,
): DCEvent[] => {
  return [
    setInCompanySettingsState("isLoading", true),
    settingsApi.uploadDocumentPlacement({
      orgNo,
      data: { logoPlacement },
      onSuccess: () => [
        loadCompanySettings(orgNo),
        setInCompanySettingsState("isLoading", undefined),
        setInCompanySettingsState("editField", undefined),
        showGeneralSuccess(),
      ],
      onFailure: () => [
        setInCompanySettingsState("isLoading", undefined),
        setInCompanySettingsState("editField", undefined),
        showGeneralError(),
      ],
    }),
  ];
};

export const onUploadDocumentLogo = (
  dispatch: (events: DCEvent[]) => void,
  orgNo: string,
  selectedFiles: File[],
  fileConfig: FileUploadConfig,
) => {
  const onUpload = (files: File[]) => {
    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    reader.onload = () => {
      const logoBase64 = reader.result?.toString() ?? "";
      dispatch([
        setInCompanySettingsState("isLoading", true),
        settingsApi.uploadedDocumentLogo({
          orgNo,
          data: { logoBase64 },
          onSuccess: () => [
            loadCompanySettings(orgNo),
            setInCompanySettingsState("isLoading", undefined),
            setInCompanySettingsState("editField", undefined),
            showGeneralSuccess(),
          ],
          onFailure: () => [
            setInCompanySettingsState("isLoading", undefined),
            setInCompanySettingsState("editField", undefined),
            showGeneralError(),
          ],
        }),
      ]);
    };
  };
  if (selectedFiles.length) {
    validateFiles(selectedFiles, fileConfig)
      .then(onUpload)
      .catch((err) => {
        if (isFileUploadValidationError(err)) {
          dispatch([
            setInCompanySettingsState("editField", undefined),
            showError(createFileTypeValidationErrorMessage(err)),
          ]);
        }
      });
  }
};

export const onOpenRemoveDocumentLogoDialog = (): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "removeDocumentLogoDialog",
  }),
];

export const onRemoveDocumentLogo = (orgNo: string): DCEvent[] => [
  setInCompanySettingsState("isLoading", true),
  settingsApi.deleteDocumentLogo({
    orgNo,
    onSuccess: () => [
      loadCompanySettings(orgNo),
      setInCompanySettingsState("isLoading", undefined),
      setInCompanySettingsState("editField", undefined),
      showGeneralSuccess(),
    ],
    onFailure: () => [
      setInCompanySettingsState("isLoading", undefined),
      setInCompanySettingsState("editField", undefined),
      showGeneralError(),
    ],
  }),
];

export const addWhitelabel = (): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "whitelabel",
    whitelabelModel: new StringFieldModel(),
  }),
];

export const onEditWhitelabel = (value?: string): DCEvent[] => [
  setInCompanySettingsState("editField", {
    type: "whitelabel",
    whitelabelModel: new StringFieldModel({
      value,
    }),
  }),
];

export const onRemoveWhitelabel = (orgNo: string) => [
  settingsApi.saveWhitelabel({
    orgNo,
    data: {},
    onSuccess: () => [
      loadCompanySettings(orgNo),
      setInCompanySettingsState("isLoading", undefined),
      showGeneralSuccess(),
    ],
    onFailure: () => [
      setInCompanySettingsState("isLoading", undefined),
      setInCompanySettingsState("editField", undefined),
      showGeneralError(),
    ],
  }),
];

export const onSaveWhitelabel = (orgNo: string, whitelabelName: string): DCEvent[] => [
  setInCompanySettingsState("isLoading", true),
  settingsApi.saveWhitelabel({
    orgNo,
    data: { whitelabelName },
    onSuccess: () => [
      loadCompanySettings(orgNo),
      setInCompanySettingsState("isLoading", undefined),
      setInCompanySettingsState("editField", undefined),
      showGeneralSuccess(),
    ],
    onFailure: () => [
      setInCompanySettingsState("isLoading", undefined),
      setInCompanySettingsState("editField", undefined),
      showError({ key: "settings.whitelabel-not-found", params: { n: whitelabelName } }),
    ],
  }),
];

export const onRevokeAdminAccess = (orgNo: string, admin: AdminItem): DCEvent[] => [
  settingsApi.revokeAdminAccess({
    orgNo,
    externalId: admin.id,
    onSuccess: () => [loadUsersWithAdminAccess(orgNo), showGeneralSuccess()],
    onFailure: () => [showGeneralError()],
  }),
];

export const openSendInvitationDialog = ({
  organisationNumber,
  companyName,
  role,
  email,
  customRoles,
}: {
  organisationNumber: string;
  companyName: string;
  role?: CustomRole;
  email?: string;
  customRoles?: CustomRoleItem[];
}): DCEvent[] => {
  const hasSharedCustomRole =
    role !== undefined &&
    customRoles?.find((f) => f.role.customRoleId === role.customRoleId)?.companies.length === 1;
  return [
    {
      type: "showSendInvitationDialog",
      data: {
        invitationType: InvitationType.USER,
        organisationNumber,
        companyName,
        lang: defaultLanguageForOrgNo(organisationNumber),
        title:
          role !== undefined
            ? { key: "roles.invite-new", params: { roleName: role.customRoleName } }
            : { key: "invite-user.title" },
        adminInfo: hasSharedCustomRole
          ? {
              key: "invite-user.custom-role-tip-share",
              params: {
                contactsUrl: "/portfolio/all/contacts/admins",
              },
            }
          : {
              key: "invite-user.admin.info",
              params: {
                url:
                  role !== undefined
                    ? `/portfolio/all/contacts/custom-role/${role.customRoleId}`
                    : "/portfolio/all/contacts/admins",
              },
            },
        description:
          role !== undefined
            ? undefined
            : { key: "invite-user.infoMessage", params: { companyName } },
        roleId: role?.customRoleId,
        customRoleName: role?.customRoleName,
        email,
      },
    },
  ];
};

const prepMenuItems = (
  state: CompanySettingsState = defaultCompanySettingsState(),
  orgNo: string,
): CompanySettingsMenuItem[] =>
  companySettingsMenuItemTypes(orgNo).map((type) => ({
    type,
    title: companySettingsMenuItemLabels[type],
    iconName: companySettingsMenuItemIcons[type],
    isSelected: state.selectedMenuItem === type,
  }));

export const updateCompanySettingsStore = (
  companySettings: Partial<CompanySettingsState>,
): DCEvent => ({
  type: "update-store",
  data: { companySettings: { ...defaultCompanySettingsState(), ...companySettings } },
});

const loadCompanySettings = (orgNo: string): HttpEvent<GetCompanySettingsResponse> =>
  settingsApi.getCompanySettings({
    orgNo,
    onSuccess: (res) => [
      setInCompanySettingsState("isLoading", undefined),
      setInCompanySettingsState("response", res.data),
    ],
    onFailure: clearLoadingAndShowGeneralError,
  });

const loadOnboardingStatusForCompanySettings = (
  orgNo: string,
): HttpEvent<OnboardingStatusResponse> =>
  settingsApi.getOnboardingStatus({
    orgNo,
    onSuccess: (res) => [setInCompanySettingsState("onboardStatusResult", res.data)],
    onFailure: () => [showGeneralError()],
  });

const loadUsersWithAdminAccess = (orgNo: string): HttpEvent<ListAdminAccessResponse> =>
  settingsApi.getUsersWithAdminAccess({
    orgNo,
    onSuccess: (res) => [setInCompanySettingsState("usersResponse", res.data)],
    onFailure: () => [showGeneralError()],
  });

const loadAltinnSystemUsers = (orgNo: string): HttpEvent<AltinnSystemUsersResponse> =>
  settingsApi.getAltinnSystemUsers({
    orgNo,
    onSuccess: (res) => [setInCompanySettingsState("altinnSystemUsers", res.data)],
    onFailure: () => [showGeneralError()],
  });

const loadBillingInfo = (orgNo: string) =>
  settingsApi.getEnterpriseAccountsForUser({
    orgNo,
    onSuccess: (res) => [
      setInCompanySettingsState("enterpriseAccountsResponse", res.data),
      loadSubscriptionInfo(orgNo),
    ],
    onFailure: () => [showGeneralError()],
  });

const loadSubscriptionInfo = (orgNo: string): HttpEvent<AdminItem[]> =>
  settingsApi.findSubscription({
    orgNo,
    onSuccess: (res) => [setInCompanySettingsState("subscriptionResponse", res.data)],
    onFailure: () => [showGeneralError()],
  });

export const onSaveEnterpriseAccount = (
  orgNo: string,
  currentProductName: ProductName | undefined,
  request: UpdateEnterpriseAccountRequest,
): DCEvent[] => {
  const save = [
    settingsApi.saveEnterpriseAccount({
      orgNo,
      request,
      onSuccess: () => [
        setInCompanySettingsState("editField", undefined),
        loadSubscriptionInfo(orgNo),
        { type: "update-company-info" },
      ],
      onFailure: () => [showGeneralError()],
    }),
  ];
  return isDowngradeToOwnershipRegister(currentProductName, request.productName)
    ? confirmDowngradeToOwnershipRegister(save)
    : save;
};

const onRemoveFromAccount = (orgNo: string) => [
  settingsApi.removeFromAccount({
    orgNo,
    onSuccess: () => [loadSubscriptionInfo(orgNo), { type: "update-company-info" }],
    onFailure: () => [showGeneralError()],
  }),
];

const confirmDangerZoneDelete = (orgNo: string, state: CompanySettingsState): DCEvent[] => {
  if (state.editField?.type === "dangerZoneDelete") {
    if (state.editField.confirmationInput.inputValue.toLowerCase().trim() !== "delete") {
      return [];
    }
    const onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[] = () => [
      setInCompanySettingsState("editField", undefined),
      showGeneralSuccess(),
    ];
    return compact([
      state.editField.dangerZone === "deleteTransactions" &&
        settingsApi.deleteShareholderRegister({
          orgNo,
          onSuccess,
          onFailure: () => [showGeneralError()],
        }),
      state.editField.dangerZone === "deleteTransactionsAndShareholders" &&
        settingsApi.deleteShareholdersAndTransactions({
          orgNo,
          onSuccess,
          onFailure: () => [showGeneralError()],
        }),
      state.editField.dangerZone === "deregisterCompany" &&
        settingsApi.deregisterCompany({
          orgNo,
          onSuccess,
          onFailure: () => [showGeneralError()],
        }),
      state.editField.dangerZone === "deleteAltinnSubmissions" &&
        settingsApi.deleteAltinnSubmissions({
          orgNo,
          onSuccess,
          onFailure: () => [showGeneralError()],
        }),
    ]);
  } else {
    return [];
  }
};

export const initCompanySettingsPage = (
  { menuItem }: CompanySettingsRouteParams,
  orgNo: string,
  state: CompanySettingsState | undefined,
  userRole: CustomRoleForUser,
  isAdmin: boolean,
): DCEvent[] => {
  const companySettingsState: CompanySettingsState = {
    ...(state ??
      defaultCompanySettingsState({ menuItem: menuItem as CompanySettingsMenuItemType })),
  };
  const selectedMenuItem: CompanySettingsMenuItemType = menuItem
    ? (menuItem as CompanySettingsMenuItemType)
    : companySettingsState.selectedMenuItem;
  return compact([
    updateCompanySettingsStore({
      ...companySettingsState,
      isLoading: true,
      selectedMenuItem,
      altinnSystemUsers: undefined,
      canEdit: userRole.allowCompanySettingsEdit,
      isAdmin,
    }),
    loadCompanySettings(orgNo),
    loadOnboardingStatusForCompanySettings(orgNo),
    (menuItem === "users" || menuItem === "permissions") && loadUsersWithAdminAccess(orgNo),
    selectedMenuItem === "altinn" && loadAltinnSystemUsers(orgNo),
    loadBillingInfo(orgNo),
  ]);
};

export const defaultCompanySettingsState = ({
  isLoading,
  menuItem,
}: {
  isLoading?: boolean;
  menuItem?: CompanySettingsMenuItemType;
} = {}): CompanySettingsState => ({
  isLoading,
  selectedMenuItem: menuItem ?? "company",
  selectedUserRoleFilter: allRoles,
  selectedCompaniesUsingRole: [],
  inviteAccountantEmail: new StringFieldModel({ required: true, validator: emailValidator }),
});

const setInCompanySettingsState = <K extends keyof CompanySettingsState>(
  key: K,
  valueOrCallback:
    | CompanySettingsState[K]
    | ((
        current: CompanySettingsState,
        currentValue: CompanySettingsState[K],
      ) => CompanySettingsState[K] | undefined),
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    companySettings: (companySettings = defaultCompanySettingsState()) => ({
      ...companySettings,
      [key]: isFunction(valueOrCallback)
        ? valueOrCallback(companySettings, companySettings[key])
        : valueOrCallback,
    }),
  },
});

type GetCompanySettingsRequest = {
  orgNo: string;
  onSuccess: (res: HttpResponse<GetCompanySettingsResponse>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type GetOnboardingStatus = {
  orgNo: string;
  onSuccess: (res: HttpResponse<OnboardingStatusResponse>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type RequestAltinnSystemAccess = {
  orgNo: string;
  onSuccess: (res: HttpResponse<RequestSystemUserResponse>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type OnboardingCompany = {
  orgNo: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type PostUpdateCompanyInfoRequest = {
  orgNo: string;
  data: UpdateCompanyInfoRequest;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type GetAltinnSystemUsersRequest = {
  orgNo: string;
  onSuccess: (res: HttpResponse<AltinnSystemUsersResponse>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type GetUsersWithAdminAccessRequest = {
  orgNo: string;
  onSuccess: (res: HttpResponse<ListAdminAccessResponse>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type PreviewCustomRoleRequest = {
  roleId: string;
  orgNo: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type RevokeUserAdminAccessRequest = {
  orgNo: string;
  externalId: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type AddNewTaxAssessmentValueRequest = {
  orgNo: string;
  taxAssessmentValue: Amount;
  year: number;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateTaxAssessmentValueRequest = {
  orgNo: string;
  data: TaxAssessmentValue;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type DeleteTaxAssessmentValueRequest = {
  orgNo: string;
  taxAssessmentValueId: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type AddNewFairValueRequest = {
  orgNo: string;
  fairValue: Amount;
  fairValueDate: string;
  shareTypeId?: number;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateFairValueRequest = {
  orgNo: string;
  data: FairValue;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type DeleteFairValueRequest = {
  orgNo: string;
  fairValueId: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateDocumentLanguageRequest = {
  orgNo: string;
  data: { defaultDocumentLanguages: Language[] };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateDocumentFontRequest = {
  orgNo: string;
  data: { font?: DocumentFont };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UploadDocumentLogoRequest = {
  orgNo: string;
  data: { logoBase64: string };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type DeleteDocumentLogoRequest = {
  orgNo: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UploadDocumentPlacementRequest = {
  orgNo: string;
  data: { logoPlacement: LogoPlacementOption };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateWhiteLabelRequest = {
  orgNo: string;
  data: { whitelabelName?: string };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type CreateNewRoleRequest = {
  orgNo: string;
  data: NewCustomRoleRequest;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateRoleRequest = {
  orgNo: string;
  data: UpdateCustomRoleRequest;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type DeleteRoleRequest = {
  orgNo: string;
  roleId: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type RevokeAccessToRoleRequest = {
  orgNo: string;
  userId: string;
  customRoleId: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type WithOrgNoRequest = {
  orgNo: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateCompanySettingsRequest = {
  orgNo: string;
  data: { isShareholderPortalEnabled?: boolean; overrideCostPriceDisplay?: boolean };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateCompanyStatusRequest = {
  orgNo: string;
  data: { status: CompanyStatus };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateDateOfIncorporationRequest = {
  orgNo: string;
  data: { dateOfIncorporation: string };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateTinNumberRequest = {
  orgNo: string;
  data: { tinNumber: string | null; effectiveFrom?: string };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateOrganisationNumberRequest = {
  companyId: string;
  data: { oldOrganisationNumber: string; newOrganisationNumber: string; effectiveFrom?: string };
  onSuccess: (res: HttpResponse<{ organisationNumber: string }>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateCurrencyRequest = {
  orgNo: string;
  data: { currency: string };
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateSigningReminderSettingsRequest = {
  orgNo: string;
  signingReminderSettings: SigningReminderSettings;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type UpdateShareholderContactRequest = {
  orgNo: string;
  data: FormData;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

type DeleteShareholderContactRequest = {
  orgNo: string;
  id: string;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

export const findShareType = (sharetypes: ShareType[], sharetypeId: number): ShareType =>
  sharetypes.find((shareType) => shareType.sharetypeId === sharetypeId)!;

export type UpdateCompanyInfoRequest = {
  newCompanyName?: string;
  effectiveFrom?: string;
};

type UpdateAddressApiRequest = {
  orgNo: string;
  request: UpdateAddressRequest;
  onSuccess: (res: HttpResponse, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
};

const addressPaths: Record<AddressType, string> = {
  BUSINESS: "business-address",
  POSTAL: "postal-address",
};

export const settingsApi = {
  getCompanySettings: ({ orgNo, onSuccess, onFailure }: GetCompanySettingsRequest): HttpEvent => ({
    type: "http",
    method: "get",
    url: `/company-settings/${orgNo}`,
    onSuccess,
    onFailure,
  }),

  getOnboardingStatus: ({ orgNo, onSuccess, onFailure }: GetOnboardingStatus): HttpEvent =>
    httpGet({
      url: `/onboarding-status/${orgNo}`,
      onSuccess,
      onFailure,
    }),

  onboardCompanySettings: ({ orgNo, onSuccess, onFailure }: OnboardingCompany): HttpEvent =>
    httpPost({
      url: `/onboarding-status/complete-company-settings/${orgNo}`,
      onSuccess,
      onFailure,
    }),

  requestStandardSystemAccess: ({
    orgNo,
    onSuccess,
    onFailure,
  }: RequestAltinnSystemAccess): HttpEvent =>
    httpPost({
      url: `/company-settings/request-standard-system-user/${orgNo}`,
      onSuccess,
      onFailure,
    }),

  requestClientSystemAccess: ({
    orgNo,
    onSuccess,
    onFailure,
  }: RequestAltinnSystemAccess): HttpEvent =>
    httpPost({
      url: `/company-settings/request-client-system-user/${orgNo}`,
      onSuccess,
      onFailure,
    }),

  getAltinnSystemUsers: ({ orgNo, onSuccess, onFailure }: GetAltinnSystemUsersRequest): HttpEvent =>
    httpGet({
      url: `/company-settings/${orgNo}/altinn-system-users`,
      onSuccess,
      onFailure,
    }),

  getUsersWithAdminAccess: ({
    orgNo,
    onSuccess,
    onFailure,
  }: GetUsersWithAdminAccessRequest): HttpEvent =>
    httpGet({
      url: `/company-settings/${orgNo}/admin-access`,
      onSuccess,
      onFailure,
    }),

  updateCompanyInfo: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: PostUpdateCompanyInfoRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-company-info`,
      data,
      onSuccess,
      onFailure,
    }),

  addNewTaxAssessmentValue: ({
    orgNo,
    taxAssessmentValue,
    year,
    onSuccess,
    onFailure,
  }: AddNewTaxAssessmentValueRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/new-tax-assessment-value`,
      data: { taxAssessmentValue, year },
      onSuccess,
      onFailure,
    }),

  updateTaxAssessmentValue: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UpdateTaxAssessmentValueRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-tax-assessment-value`,
      data,
      onSuccess,
      onFailure,
    }),

  deleteTaxAssessmentValue: ({
    orgNo,
    taxAssessmentValueId,
    onSuccess,
    onFailure,
  }: DeleteTaxAssessmentValueRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/delete-tax-assessment-value/${taxAssessmentValueId}`,
      onSuccess,
      onFailure,
    }),

  addNewFairValue: ({
    orgNo,
    fairValue,
    fairValueDate,
    shareTypeId,
    onSuccess,
    onFailure,
  }: AddNewFairValueRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/new-fair-value`,
      data: {
        fairValue,
        fairValueDate,
        shareTypeId,
      },
      onSuccess,
      onFailure,
    }),

  updateFairValue: ({ orgNo, data, onSuccess, onFailure }: UpdateFairValueRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-fair-value`,
      data,
      onSuccess,
      onFailure,
    }),

  deleteFairValue: ({
    orgNo,
    fairValueId,
    onSuccess,
    onFailure,
  }: DeleteFairValueRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/delete-fair-value/${fairValueId}`,
      onSuccess,
      onFailure,
    }),

  updateDocumentLanguage: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UpdateDocumentLanguageRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-company-default-document-languages`,
      data,
      onSuccess,
      onFailure,
    }),

  updateDocumentFont: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UpdateDocumentFontRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-company-font`,
      data,
      onSuccess,
      onFailure,
    }),

  deleteDocumentLogo: ({ orgNo, onSuccess, onFailure }: DeleteDocumentLogoRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/company-logo`,
      onSuccess,
      onFailure,
    }),

  uploadedDocumentLogo: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UploadDocumentLogoRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-company-logo`,
      data,
      onSuccess,
      onFailure,
    }),

  uploadDocumentPlacement: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UploadDocumentPlacementRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-company-logo-placement`,
      data,
      onSuccess,
      onFailure,
    }),

  saveWhitelabel: ({ orgNo, data, onSuccess, onFailure }: UpdateWhiteLabelRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-whitelabel`,
      data,
      onSuccess,
      onFailure,
    }),

  previewCustomRole: ({
    orgNo,
    roleId,
    onSuccess,
    onFailure,
  }: PreviewCustomRoleRequest): HttpEvent =>
    httpGet({
      url: `/company-settings/${orgNo}/preview-custom-role/${roleId}`,
      onSuccess,
      onFailure,
    }),

  revokeAdminAccess: ({
    orgNo,
    externalId,
    onSuccess,
    onFailure,
  }: RevokeUserAdminAccessRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/admin-access/${externalId}`,
      onSuccess,
      onFailure,
    }),

  newCustomRole: ({ orgNo, data, onSuccess, onFailure }: CreateNewRoleRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/new-custom-role`,
      data,
      onSuccess,
      onFailure,
    }),

  updateCustomRole: ({ orgNo, data, onSuccess, onFailure }: UpdateRoleRequest): HttpEvent =>
    httpPut({
      url: `/company-settings/${orgNo}/update-custom-role`,
      data,
      onSuccess,
      onFailure,
    }),

  deleteCustomRole: ({ orgNo, roleId, onSuccess, onFailure }: DeleteRoleRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/custom-role/${roleId}`,
      onSuccess,
      onFailure,
    }),

  revokeAccessToRole: ({
    orgNo,
    userId,
    customRoleId,
    onSuccess,
    onFailure,
  }: RevokeAccessToRoleRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/revoke-custom-role`,
      data: { userId, customRoleId },
      onSuccess,
      onFailure,
    }),

  findSubscription: ({ orgNo, onSuccess, onFailure }: WithOrgNoRequest): HttpEvent =>
    httpGet({
      url: `/payment/find-subscription/${orgNo}?paymentDetails=true`,
      onSuccess,
      onFailure,
    }),

  getEnterpriseAccountsForUser: ({ onSuccess, onFailure }: WithOrgNoRequest): HttpEvent =>
    httpGet({
      url: `/user/enterprise-accounts`,
      onSuccess,
      onFailure,
    }),

  deleteShareholderRegister: ({ orgNo, onSuccess, onFailure }: WithOrgNoRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/shareholder-register`,
      onSuccess,
      onFailure,
    }),

  deleteShareholdersAndTransactions: ({
    orgNo,
    onSuccess,
    onFailure,
  }: WithOrgNoRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/shareholders-and-transactions`,
      onSuccess,
      onFailure,
    }),

  deregisterCompany: ({ orgNo, onSuccess, onFailure }: WithOrgNoRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/deregister`,
      onSuccess,
      onFailure,
    }),

  deleteAltinnSubmissions: ({ orgNo, onSuccess, onFailure }: WithOrgNoRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/altinn-submissions`,
      onSuccess,
      onFailure,
    }),

  updateCompanySettings: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UpdateCompanySettingsRequest): HttpEvent =>
    httpPut({
      url: `/company-settings/${orgNo}/update`,
      data,
      onSuccess,
      onFailure,
    }),

  updateCompanyStatus: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UpdateCompanyStatusRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-status`,
      data,
      onSuccess,
      onFailure,
    }),

  updateDateOfIncorporation: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UpdateDateOfIncorporationRequest): HttpEvent =>
    httpPost({
      url: `/company-settings/${orgNo}/update-date-of-incorporation`,
      data,
      onSuccess,
      onFailure,
    }),

  updateAddress: ({ orgNo, request, onSuccess, onFailure }: UpdateAddressApiRequest): HttpEvent =>
    httpPut({
      url: `/company-settings/${orgNo}/${addressPaths[request.addressType]}`,
      data: request.data,
      onSuccess,
      onFailure,
    }),

  updateTinNumber: ({ orgNo, data, onSuccess, onFailure }: UpdateTinNumberRequest): HttpEvent =>
    httpPut({
      url: `/company-settings/${orgNo}/tin-number`,
      data,
      onSuccess,
      onFailure,
    }),

  updateOrganisationNumber: ({
    companyId,
    data,
    onSuccess,
    onFailure,
  }: UpdateOrganisationNumberRequest): HttpEvent =>
    httpPut({
      url: `/company-settings/${companyId}/organisation-number`,
      data,
      onSuccess,
      onFailure,
    }),

  updateCurrency: ({ orgNo, data, onSuccess, onFailure }: UpdateCurrencyRequest): HttpEvent =>
    httpPut({
      url: `/company-settings/${orgNo}/currency`,
      data,
      onSuccess,
      onFailure,
    }),

  changeShareholderContact: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: UpdateShareholderContactRequest): HttpEvent =>
    httpPut({
      url: `/company-settings/${orgNo}/update-shareholder-contact`,
      data,
      onSuccess,
      onFailure,
    }),

  deleteShareholderContact: ({
    orgNo,
    id,
    onSuccess,
    onFailure,
  }: DeleteShareholderContactRequest): HttpEvent =>
    httpDelete({
      url: `/company-settings/${orgNo}/delete-shareholder-contact/${id}`,
      onSuccess,
      onFailure,
    }),

  updateSigningReminderSettings: ({
    orgNo,
    signingReminderSettings,
    onSuccess,
    onFailure,
  }: UpdateSigningReminderSettingsRequest): HttpEvent =>
    httpPut({
      url: `/company-settings/${orgNo}/update-reminder-settings`,
      data: { signingReminderSettings },
      onSuccess,
      onFailure,
    }),

  saveEnterpriseAccount: ({
    orgNo,
    request,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    request: UpdateEnterpriseAccountRequest;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpPost({
      url: `/company/${orgNo}/enterprise-account`,
      data: request,
      onSuccess,
      onFailure,
    }),

  removeFromAccount: ({
    onFailure,
    onSuccess,
    orgNo,
  }: {
    orgNo: string;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }) =>
    httpDelete({
      url: `/company/${orgNo}/enterprise-account`,
      onSuccess,
      onFailure,
    }),

  updateCurrentAccountant: ({
    orgNo,
    accountantOrgNo,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    accountantOrgNo: string;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }) =>
    httpPost({
      url: `/company-settings/${orgNo}/update-accountant`,
      data: { accountantOrgNo },
      onSuccess,
      onFailure,
    }),

  updateCurrentAuditor: ({
    orgNo,
    auditorOrgNo,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    auditorOrgNo: string;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }) =>
    httpPost({
      url: `/company-settings/${orgNo}/update-auditor/${auditorOrgNo}`,
      data: auditorOrgNo,
      onSuccess,
      onFailure,
    }),

  searchCompanies: ({
    query,
    onSuccess,
    onFailure,
  }: {
    query: string;
    onSuccess: (res: HttpResponse<SuggestedCompany[]>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => httpGet({ url: `/companysearch${toQueryString({ q: query })}`, onSuccess, onFailure }),

  inviteAccountant: ({
    orgNo,
    email,
    language,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    email: string;
    language: Language;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }) =>
    httpPost({
      url: `/company-settings/${orgNo}/invite-accountant`,
      data: { recipients: [{ email }], language },
      onSuccess,
      onFailure,
    }),

  deleteAccountantAccess: ({
    orgNo,
    userId,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    userId: number;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }) =>
    httpDelete({
      url: `/company-settings/${orgNo}/accountant/${userId}`,
      onSuccess,
      onFailure,
    }),
};

const featureAccessLabels: { [key in FeatureAccess]: TMessage } = {
  MANAGE: { key: "manage" },
  EDIT: { key: "edit" },
  VIEW: { key: "view" },
  NONE: { key: "hidden" },
};

export class CustomRoleLanguageModel {
  roleName: StringFieldModel;
  description: StringFieldModel;
  language: Language;
  orgNo: string;
  data?: CustomRole;

  constructor(language: Language, orgNo: string, data?: CustomRole) {
    const customRoleLanguage = data?.customRoleLanguages.find(
      (roleLanguage) => roleLanguage.language === language,
    );
    this.roleName = new StringFieldModel({
      value: customRoleLanguage?.name ?? "",
      required: defaultLanguageForOrgNo(orgNo) === language,
    });
    this.description = new StringFieldModel({ value: customRoleLanguage?.description });
    this.language = language;
    this.orgNo = orgNo;
    this.data = data;
    makeAutoObservable(this);
  }

  get isDefaultLanguage(): boolean {
    return this.language === defaultLanguageForOrgNo(this.orgNo);
  }
}

export function findCustomRoleDescriptions(
  customRoleLanguages: CustomRoleLanguage[],
  locale: Language,
  orgNo: string,
) {
  const matchingLocaleDescription = customRoleLanguages.find(
    (customRole) => customRole.language === locale && customRole.description,
  )?.description;
  if (matchingLocaleDescription) return matchingLocaleDescription;
  const defaultLanguage = defaultLanguageForOrgNo(orgNo);
  const defaultLanguageDescription = customRoleLanguages.find(
    (customRole) => customRole.language === defaultLanguage && customRole.description,
  )?.description;

  if (defaultLanguageDescription) return defaultLanguageDescription;
  return customRoleLanguages.find((customRole) => customRole.description)?.description ?? "";
}

export function toUpdateCompanyInfoRequest(state: CompanySettingsState): UpdateCompanyInfoRequest {
  const editField = state.editField?.type === "companyName" ? state.editField : undefined;
  return {
    newCompanyName: editField?.updateModel.validatedValue,
    effectiveFrom: editField?.effectiveFromModel.validatedValue,
  };
}

function companyShareholderContactImageUrl(orgNo: string, id: string, ref: string) {
  return `/api/company-settings/${orgNo}/shareholder-contact-image/${id}/${ref}`;
}

export enum ReminderRecipient {
  DOCUMENT_SENDER = "DOCUMENT_SENDER",
  ALL_ADMINS = "ALL_ADMINS",
}

const reminderLabels: { [key in ReminderRecipient]: TMessage } = {
  [ReminderRecipient.DOCUMENT_SENDER]: { key: "settings.signing-reminder.documentSender" },
  [ReminderRecipient.ALL_ADMINS]: { key: "settings.signing-reminder.allAdmins" },
};

export const reminderRecipientModel = (
  value: ReminderRecipient,
): RadioFieldModel<ReminderRecipient> =>
  new RadioFieldModel({
    value,
    options: Object.values(ReminderRecipient),
    idFn: identity,
    labelFn: (option) => reminderLabels[option],
  });

const numericModel = (
  value: number,
  step: number = 1,
  maxValue: number = 10,
): RadioFieldModel<number> =>
  new RadioFieldModel({
    value: value,
    options: Array.from({ length: maxValue / step }, (_, i) => i * step + 1),
    idFn: identity,
    labelFn: (option) => ({ translated: option ? option.toString() : "" }),
  });

export type UpdateEnterpriseAccountRequest = {
  enterpriseAccountId: string;
  productName: string;
};

export const hasCompaniesInThisCustomRole = (
  item: PortfolioCompany,
  usersUsingRole: UserItem[],
  orgNo: string,
): boolean => {
  return (
    item.organisationNumber === orgNo ||
    usersUsingRole.some((user) => user.orgNo === item.organisationNumber)
  );
};

export type LogoPlacementOption = "center" | "right" | "left";

export type DocumentFont = {
  displayName: string;
  value: string;
};

export const roleOptionMenu = ({
  openNewOrEditRoleDialog,
  language,
}: {
  openNewOrEditRoleDialog: CompanySettingsViewProps["openNewOrEditRoleDialog"];
  language: Language;
}): DropdownMenuProps => {
  return {
    button: {
      type: "button",
      variant: "secondary",
      label: { key: "roles.add-new" },
    },
    items: [
      {
        type: "item",
        icon: "user-plus",
        label: { key: "roles.new-from-scratch" },
        onSelect: openNewOrEditRoleDialog,
      },
      { type: "separator" },
      { type: "label", label: { key: "start-from-template" } },
      ...roleTemplates.map<DropdownMenuItemProps>((template) => ({
        icon: "user",
        type: "item",
        label: {
          translated:
            template.role.customRoleLanguages.find((l) => l.language === language)?.name ?? "",
        },
        onSelect: () => openNewOrEditRoleDialog(template),
      })),
    ],
  };
};

const prepRoleTabs = ({
  tab,
  dispatch,
  primaryCompanyName,
  orgNo,
  customRoles,
}: {
  tab: CustomRoleTab;
  dispatch: EventDispatcher;
  primaryCompanyName: string;
  orgNo: string;
  customRoles: CustomRoleItem[];
}): TabProps[] => {
  const primaryCount = customRoles.filter((r) => r.role.orgNo === orgNo).length;
  const otherCount = customRoles.filter((r) => r.role.orgNo !== orgNo).length;
  const allRolesTab: TabProps = {
    id: "allRoles",
    label: { key: "all-roles" },
    isActive: tab === "allRoles",
    onSelect: () => dispatch([navigate(adminPermissionsSettingsWithTab(orgNo, "allRoles"))]),
  };
  if (otherCount === 0) return [allRolesTab];
  return [
    allRolesTab,
    {
      id: "primaryCompany",
      label: {
        key: "company.settings.custom-role.created-primary-company",
        params: { primaryCompanyName, count: primaryCount },
      },
      isActive: tab === "primaryCompany",
      onSelect: () =>
        dispatch([navigate(adminPermissionsSettingsWithTab(orgNo, "primaryCompany"))]),
    },
    {
      id: "otherCompany",
      label: {
        key: "company.settings.custom-role.created-other-company",
        params: { count: otherCount },
      },
      isActive: tab === "otherCompany",
      onSelect: () => dispatch([navigate(adminPermissionsSettingsWithTab(orgNo, "otherCompany"))]),
    },
  ];
};

export const mapToCustomRoleTab = (roleTab?: string): CustomRoleTab => {
  switch (roleTab) {
    case "primaryCompany":
      return "primaryCompany";
    case "otherCompany":
      return "otherCompany";
    default:
      return "allRoles";
  }
};

export const roleRowIconName = (row: RoleRow, orgNo: string): IconName => {
  switch (row.type) {
    case "admin":
      return "globe";
    case "custom":
      return row.item.role.orgNo === orgNo ? "buildings" : "briefcase";
  }
};

export function prepRoleRows({
  customRoles,
  roleTab,
  orgNo,
}: {
  customRoles: CustomRoleItem[];
  roleTab: CustomRoleTab;
  orgNo: string;
}): RoleRow[] {
  const customRows: RoleRow[] = customRoles
    .filter((r) => {
      if (roleTab === "primaryCompany") return r.role.orgNo === orgNo;
      if (roleTab === "otherCompany") return r.role.orgNo !== orgNo;
      return true;
    })
    .map((r) => ({ type: "custom", item: r }));

  return roleTab === "allRoles" ? [{ type: "admin" }, ...customRows] : customRows;
}

export const prepSharingColumnValue = (row: RoleRow): TextWithTooltipProps => {
  const companyItems =
    row.type === "custom" ? otherCompaniesSharingRole(row.item.companies, row.item.role.orgNo) : [];
  return row.type === "admin"
    ? { text: { translated: "-" } }
    : companyItems.length === 0
      ? { text: { key: "company.settings.custom-roles.not-shared" } }
      : {
          tooltip: { type: "md-list", value: companyItems.map((c) => c.companyName) },
          text: { translated: String(companyItems.length) },
        };
};

export function prepRoleRowActions({
  row,
  props,
  language,
}: {
  row: RoleRow;
  props: Pick<
    CompanySettingsViewProps,
    | "openSendInvitationDialog"
    | "previewCustomRole"
    | "openNewOrEditRoleDialog"
    | "deleteRole"
    | "roleDetailsDialog"
    | "canEdit"
    | "orgNo"
  >;
  language: Language;
}): RowAction[] {
  if (!props.canEdit || row.type !== "custom") return [];
  const isCreatedByOtherCompany = row.item.role.orgNo !== props.orgNo;
  const detailsItem: DropdownMenuItems = isCreatedByOtherCompany
    ? [
        {
          type: "item",
          icon: "info",
          label: { key: "custom-roles.details" },
          onSelect: () => props.roleDetailsDialog(row.item, language),
        },
      ]
    : [];
  const editItems: DropdownMenuItems = isCreatedByOtherCompany
    ? []
    : [
        {
          type: "item",
          icon: "gear",
          label: { key: "custom-roles.settings" },
          onSelect: () => props.openNewOrEditRoleDialog(row.item),
        },
        {
          type: "item",
          color: "error",
          icon: "trash",
          label: { key: "delete" },
          onSelect:
            row.item.availableUsers.length > 0
              ? alertAction({
                  text: {
                    key: "custom-roles.cannotDelete",
                    params: {
                      name: getRoleNameByLanguage(row.item.role, language),
                      userCount: row.item.availableUsers.length,
                    },
                  },
                })
              : confirmAction({
                  text: {
                    key: "custom-roles.confirmDelete",
                    params: { name: getRoleNameByLanguage(row.item.role, language) },
                  },
                  onConfirm: props.deleteRole(row.item.role),
                }),
        },
      ];
  return [
    [
      "context-menu",
      [
        {
          type: "item",
          icon: "user-plus",
          label: {
            key: "roles.invite-new",
            params: { roleName: getRoleNameByLanguage(row.item.role, language) },
          },
          onSelect: () => props.openSendInvitationDialog(row.item.role),
        },
        {
          type: "item",
          icon: "arrow-square-out",
          label: { key: "preview" },
          onSelect: () => props.previewCustomRole(row.item.role),
        },
        ...detailsItem,
        ...editItems,
      ],
    ],
  ];
}
